/* eslint-disable react/no-unescaped-entities */
/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  type FC,
  type MouseEvent as ReactMouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { useIsMobile } from "../../../../hooks/useIsMobile";
import { type ColumnDef } from "@tanstack/react-table";
import styled from "styled-components";
import { DateTime, Duration } from "luxon";
import {
  PageTitle,
  ReactTableComponent,
  Typography,
  TypographyVariant,
  Button,
  ButtonVariant,
} from "../../../../components/ui-kit";
import { AnimateAppearanceWrapper } from "../../../../components/animate-appearence/AnimateAppearanceWrapper";
import api from "../../../../util/api";
import {
  NotificationTypes,
  useNotification,
} from "../../../../hooks/useNotification";
import { AppRoutes } from "../../../../constants/routes";
import { Paginator } from "../../../../components/ui-kit/pagination/Paginator";
import InitialBillingSetupModal, {
  type InitialBillingSetupRow,
} from "./InitialBillingSetupModal";
import {
  Container,
  TopLine,
  TableContainer,
  TableHeader,
  TableHeaderLeft,
  TableHeaderRight,
  ResponsiveWrapper,
  SearchInput,
  FilterPill,
  Cell,
  SubTag,
  TypeTag,
  LoadingWrap,
  DateCell,
  DateSub,
  formatPence,
  formatPenceShort,
  getTypeColor,
  getTypeLabel,
} from "./sharedTableStyles";

// ─────────────────────────────────────────────────────────────────────────────
// Types matching the backend response
// ─────────────────────────────────────────────────────────────────────────────

interface NextCronRunConfig {
  dayOfMonth: number;
  hour: number;
  minute: number;
  timezone: string;
}

interface UpcomingChargeRow {
  companyId: string;
  companyName: string;
  automatedBillingEnabled: boolean;
  subscription: {
    _id: string;
    subscriptionOption: string;
    subscriptionLevel: string | null;
    subscriptionInterval: string;
    status: string;
    currentPeriodStart: string | null;
    currentPeriodEnd: string | null;
    levelUsed: number;
    unitCount: number;
    cancelAtPeriodEnd: boolean;
    lastEndOfPeriodChargedAt: string | null;
  };
  companyPricing: {
    customFlatFeePerTransaction: number | null;
    customPercentageFee: number | null;
    customPercentageFeeMinimum: number | null;
    paymentsInterimBillingInterval: string | null;
    discountPercentOff: number | null;
    discountDuration: string | null;
    discountDurationInMonths: number | null;
    hasBespokePricing: boolean;
    hasHighRiskSubscriptionPricing: boolean;
    trialPeriodMode: string | null;
    trialPeriodDays: number | null;
  };
  pricingRule: string;
  interimInterval: string | null;
  preview: {
    /** VAT-inclusive total — what the cron / provider actually charges
     *  the merchant. Use this for the "Computed" headline figure. */
    subtotalPence: number;
    /** Pre-VAT, post-discount amount. Optional for backward compat —
     *  older builds of the backend may not surface this. */
    netSubtotalPence?: number;
    /** VAT amount applied (20% UK), 0 for custom-percentage
     *  PAYMENTS_ONLY and bespoke merchants. Optional. */
    vatPence?: number;
    lineItems: Array<{
      description: string;
      quantity: number;
      unitAmountPence: number;
      totalPence: number;
      type: string;
    }>;
    /** Tier ladder for PAYMENTS_ONLY tier-based merchants. Populated by
     *  the backend preview when the merchant isn't on custom fees and
     *  the chosen plan has a GBP tiered price. `matchedUpTo` is the
     *  `up_to` value (pounds) of the tier this merchant landed in, or
     *  null when volume exceeded all bounded tiers. */
    tierBreakdown?: {
      tiers: Array<{
        upTo: number | null;
        flatAmountPence: number | null;
        unitAmountDecimal: string | null;
      }>;
      volumeMinor: number;
      matchedUpTo: number | null;
      isHighRisk: boolean;
    };
  };
  willChargeAtNextRun: boolean;
  needsInitialSetup: boolean;
  paymentMethod: {
    provider: string;
    status: string;
    last4: string | null;
    brand: string | null;
    expiryMonth: number | null;
    expiryYear: number | null;
  } | null;
  /** Pending one-shot manual override that the next cron run will bill
   *  as-is. Null when no override is set. */
  manualOverwrite?: {
    amountPence: number;
    reason: string | null;
    setAt: string | null;
  } | null;
  /** When `manualOverwrite` is non-null, this carries the regular
   *  computed preview so the admin can see what we WOULD have billed. */
  computedPreview?: {
    subtotalPence: number;
    lineItems: Array<{
      description: string;
      quantity: number;
      unitAmountPence: number;
      totalPence: number;
      type: string;
    }>;
  } | null;
}

interface UpcomingChargesResponse {
  nextCronRun: NextCronRunConfig;
  totals: {
    totalCompanies: number;
    enabledCompanies: number;
    enabledChargingCompanies: number;
    /** Pre-VAT, post-discount sum across enabled-charging rows. The
     *  banner displays this for revenue-shaping; gross is in
     *  `enabledTotalPence`. Optional for older backend builds (falls
     *  back to derivation: total − VAT). */
    enabledNetPence?: number;
    /** VAT component (sum of `vatPence` per row). */
    enabledVatPence?: number;
    /** VAT-inclusive sum across enabled-charging rows — what the cron /
     *  provider will actually pull. */
    enabledTotalPence: number;
    pendingCompanies: number;
    // "Pending enable" — the £ + count of NOT-yet-enabled rows that
    // WOULD be billed at the next cron tick if their
    // `automatedBillingEnabled` flag were flipped on. Surfaces the
    // cutover backlog at a glance: "we're holding back £X of
    // billing behind the enable gate."
    pendingChargingCompanies: number;
    /** Pre-VAT, post-discount sum across pending-charging rows. */
    pendingNetPence?: number;
    /** VAT component of `pendingTotalPence`. */
    pendingVatPence?: number;
    /** VAT-inclusive sum. */
    pendingTotalPence: number;
  };
  rows: UpcomingChargeRow[];
}

// ─────────────────────────────────────────────────────────────────────────────
// Local-only styled (banner is unique to this dashboard)
// ─────────────────────────────────────────────────────────────────────────────

const Banner = styled.div`
  background: linear-gradient(135deg, #fff 0%, #f5f8fc 100%);
  border: 1px solid #d8e1ec;
  border-left: 6px solid #027aff;
  border-radius: 12px;
  padding: 22px 28px;
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 24px;
  align-items: center;
  margin-bottom: 20px;
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

// Scope notice above the main banner. Admins repeatedly asked "where
// are companies X / Y / Z?" after we narrowed the dashboard back to
// billable subs only (`active` + `trialing`) — companies in
// `payment_failed`, `past_due`, `processing`, `incomplete`,
// `canceled`, `deleted`, `incomplete_expired` deliberately don't show
// here because the cron won't bill them at the next run. The notice
// makes the scope explicit so the absence isn't read as a bug.
const ScopeNotice = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  background: #eef6ff;
  border: 1px solid #c7defb;
  border-radius: 8px;
  padding: 10px 14px;
  margin-bottom: 16px;
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  line-height: 1.5;
  color: #1f3a64;
`;

const ScopeNoticeIcon = styled.span`
  font-weight: 700;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #027aff;
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  flex-shrink: 0;
  margin-top: 1px;
`;

const ScopeNoticeStrong = styled.strong`
  color: #13273f;
`;

const BannerLabel = styled.div`
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #6b7280;
`;

const BannerScheduleLine = styled.div`
  font-family: Montserrat, sans-serif;
  font-size: 22px;
  font-weight: 700;
  color: #13273f;
  margin-top: 4px;
`;

const BannerCountdown = styled.div<{ $urgent?: boolean }>`
  font-family: Montserrat, sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: ${(p) => (p.$urgent ? "#dc2626" : "#027aff")};
  margin-top: 6px;
`;

// 4 stat cards on the banner now: Will charge / Pending to collect /
// Enabled / Need attention. The old `repeat(3, ...)` left the 4th
// card orphaned on its own row, which read as broken. Bump to 4
// columns on wide screens, fall back gracefully:
//   - wide  (>=1100px) : 4 across
//   - tablet (700-1099): 2 × 2
//   - phone  (<700)     : 2 across (still grid, narrower cards)
// `minmax(120px, 1fr)` lets the cards squeeze a bit before wrapping
// — the Will charge / Pending tiles can run up to "£999.9k" so we
// need ~120px to render the value without truncation.
const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(120px, 1fr));
  gap: 12px;
  @media (max-width: 1099px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const StatCard = styled.div<{ $accent: string }>`
  border: 1px solid #e8eaef;
  border-top: 3px solid ${(p) => p.$accent};
  border-radius: 8px;
  padding: 10px 14px;
  background: #fff;
  // Stretch + column layout aligns the value/sub vertically across
  // cards even when some labels wrap to two lines and others don't.
  // Without this, "Pending to collect" (2-line label) and "Enabled"
  // (1-line label) would put their values on different baselines,
  // which is what the user flagged as misaligned.
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
`;

const StatLabel = styled.div`
  font-family: Montserrat, sans-serif;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #8b95a2;
  // Reserve two lines worth of vertical space so labels of different
  // lengths ("Enabled" vs "Pending to collect") render with their
  // value on the same baseline across all 4 cards. ~2.4em ≈ 2 lines
  // at the 1.2 default line-height.
  min-height: 2.4em;
  line-height: 1.2;
  display: flex;
  align-items: flex-start;
`;

const StatValue = styled.div`
  font-family: Montserrat, sans-serif;
  font-size: 22px;
  font-weight: 700;
  color: #13273f;
  margin-top: 2px;
  line-height: 1.1;
`;

const StatSub = styled.div`
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  color: #8b95a2;
  margin-top: 2px;
  line-height: 1.3;
`;

const Switch = styled.label<{ $checked: boolean }>`
  position: relative;
  display: inline-block;
  width: 38px;
  height: 22px;
  cursor: pointer;
  & input {
    opacity: 0;
    width: 0;
    height: 0;
  }
  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background: ${(p) => (p.$checked ? "#10b981" : "#d1d5db")};
    border-radius: 22px;
    transition: 0.2s;
  }
  &::after {
    content: "";
    position: absolute;
    top: 3px;
    left: ${(p) => (p.$checked ? "19px" : "3px")};
    width: 16px;
    height: 16px;
    background: #fff;
    border-radius: 50%;
    transition: 0.2s;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  }
`;

const ActionGroup = styled.div`
  display: inline-flex;
  gap: 6px;
  flex-wrap: nowrap;
  white-space: nowrap;
`;

// Computed-amount cell rendered as a button so the admin can click
// through to the invoice preview modal. We keep the visual the same as
// the old plain text (bold, green/grey based on whether it'll charge)
// and add a subtle dotted underline to suggest interactivity.
const ComputedAmountBtn = styled.button<{ $charging: boolean }>`
  background: transparent;
  border: 0;
  padding: 0;
  margin: 0;
  cursor: pointer;
  font-weight: 700;
  font-size: 14px;
  color: ${(p) => (p.$charging ? "#16a34a" : "#9ca3af")};
  border-bottom: 1px dotted ${(p) => (p.$charging ? "#16a34a" : "#9ca3af")};
  &:hover {
    opacity: 0.8;
  }
  &:focus-visible {
    outline: 2px solid #2563eb;
    outline-offset: 2px;
  }
`;

// Bulk action bar — shown above the table when one or more rows are
// selected via the row-checkbox column. Sticks visually to the table
// header so the admin sees the count + actions in one viewport.
const BulkBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 10px 16px;
  margin: 0 0 12px;
  background: #eef6ff;
  border: 1px solid #bfdbfe;
  border-radius: 8px;
  font-size: 13px;
  color: #1e3a8a;
  @media (max-width: 767px) {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
  }
`;

const BulkBarLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const BulkBarRight = styled.div`
  display: flex;
  gap: 8px;
  @media (max-width: 767px) {
    flex-wrap: wrap;
    > * {
      flex: 1 1 100%;
      white-space: nowrap;
    }
  }
`;

const IconButton = styled.button`
  background: #fff;
  border: 1px solid #dbe3eb;
  border-radius: 6px;
  padding: 6px 10px;
  font-size: 12px;
  font-weight: 500;
  color: #495b6c;
  cursor: pointer;
  font-family: Montserrat, sans-serif;
  white-space: nowrap;
  transition: all 0.15s;
  &:hover {
    border-color: #027aff;
    color: #027aff;
  }
`;

const PrimaryIconButton = styled(IconButton)`
  background: #027aff;
  border-color: #027aff;
  color: #fff;
  &:hover {
    background: #0066d9;
    border-color: #0066d9;
    color: #fff;
  }
`;

const CompanyLinkBtn = styled.button`
  background: none;
  border: none;
  padding: 0;
  color: #027aff;
  font-weight: 600;
  cursor: pointer;
  font-size: 13px;
  font-family: Montserrat, sans-serif;
  text-align: left;
  &:hover {
    text-decoration: underline;
  }
`;

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

const fmtDate = (iso: string | null) =>
  iso ? DateTime.fromISO(iso).toFormat("d LLL yyyy") : "—";

function nextCronAt(cfg: NextCronRunConfig): DateTime {
  const nowZ = DateTime.now().setZone(cfg.timezone);
  let candidate = nowZ.set({
    day: cfg.dayOfMonth,
    hour: cfg.hour,
    minute: cfg.minute,
    second: 0,
    millisecond: 0,
  });
  if (candidate <= nowZ) {
    candidate = candidate.plus({ months: 1 }).set({ day: cfg.dayOfMonth });
  }
  return candidate;
}

function formatCountdown(dur: Duration | null): string {
  if (!dur || !dur.isValid) return "—";
  const ms = dur.toMillis();
  if (ms <= 0) return "now";
  const days = Math.floor(ms / 86_400_000);
  const hours = Math.floor((ms % 86_400_000) / 3_600_000);
  const minutes = Math.floor((ms % 3_600_000) / 60_000);
  const seconds = Math.floor((ms % 60_000) / 1000);
  if (days > 0) return `in ${days}d ${hours}h ${minutes}m`;
  if (hours > 0) return `in ${hours}h ${minutes}m ${seconds}s`;
  return `in ${minutes}m ${seconds}s`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Sync debug panel
//
// Renders the full GoCardless sync response below the table so the admin
// can see exactly what happened without opening the browser console:
//   - Top-line counters (linked / matched / no-match / no-mandate)
//   - Every Fena company that didn't link, with the emails the sync tried
//     and the reason it skipped
//   - Every GC customer the sync pulled (capped at 200), with the email,
//     the metadata.registered_email, and whether it has a usable mandate
//
// Useful for "I created the customer manually but it didn't sync" cases —
// you can scan the GC customers list and confirm the customer is even
// visible to the sync, then check whether the email matches the Fena
// company's createdBy/publicEmail.
// ─────────────────────────────────────────────────────────────────────────────

const SyncDebugPanelWrapper = styled.div`
  margin-top: 24px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 20px 24px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
`;

const SyncDebugHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
`;

const SyncDebugTitle = styled.h3`
  margin: 0;
  font-size: 15px;
  font-weight: 700;
  color: #111827;
`;

const SyncDebugCloseBtn = styled.button`
  background: transparent;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 4px 10px;
  font-size: 12px;
  cursor: pointer;
  color: #6b7280;
  &:hover {
    background: #f3f4f6;
  }
`;

const SyncDebugCounters = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 12px;
  margin-bottom: 16px;
`;

const SyncDebugCounter = styled.div<{ $accent?: string }>`
  background: #f9fafb;
  border-radius: 8px;
  padding: 10px 12px;
  border-left: 3px solid ${(p) => p.$accent || "#9ca3af"};
`;

const SyncDebugCounterLabel = styled.div`
  font-size: 11px;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.4px;
`;

const SyncDebugCounterValue = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: #111827;
  margin-top: 2px;
`;

const SyncDebugSection = styled.div`
  margin-top: 16px;
`;

const SyncDebugSectionTitle = styled.div`
  font-size: 12px;
  font-weight: 700;
  color: #374151;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin-bottom: 6px;
`;

const SyncDebugTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
  th,
  td {
    padding: 6px 8px;
    border-bottom: 1px solid #f1f3f5;
    text-align: left;
    vertical-align: top;
  }
  th {
    color: #6b7280;
    font-weight: 600;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.4px;
  }
  td {
    color: #111827;
  }
`;

const Pill = styled.span<{ $tone: "ok" | "warn" | "err" | "muted" }>`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  background: ${(p) =>
    p.$tone === "ok"
      ? "#d1fae5"
      : p.$tone === "warn"
      ? "#fef3c7"
      : p.$tone === "err"
      ? "#fee2e2"
      : "#f3f4f6"};
  color: ${(p) =>
    p.$tone === "ok"
      ? "#065f46"
      : p.$tone === "warn"
      ? "#92400e"
      : p.$tone === "err"
      ? "#991b1b"
      : "#6b7280"};
`;

interface SyncResult {
  gcCustomersFetched?: number;
  companiesScanned?: number;
  alreadyLinked?: number;
  matchedByRegisteredEmail?: number;
  matchedByBasicEmail?: number;
  matchedByPublicEmail?: number;
  noEmail?: number;
  noGcMatch?: number;
  noMandate?: number;
  linked?: number;
  healedToActive?: number;
  errors?: number;
  misses?: Array<{
    companyId: string;
    companyName?: string;
    triedEmails: string[];
    reason: "no_email" | "no_gc_match" | "no_mandate" | "already_linked";
  }>;
  gcCustomersSummary?: Array<{
    id: string;
    email: string | null;
    registeredEmail: string | null;
    /** Raw metadata as GC returned it. When `registeredEmail` is null
     *  but the GC customer DOES have metadata, this column shows what
     *  keys/values are stored — useful when ops typed the field under
     *  an unrecognised key (e.g. "registered email" with a space). */
    metadata?: Record<string, string> | null;
    givenName?: string;
    familyName?: string;
    companyName?: string;
    hasUsableMandate?: boolean;
    mandateStatus?: string | null;
  }>;
}

const SyncDebugPanel: FC<{ result: SyncResult; onClose: () => void }> = ({
  result,
  onClose,
}) => {
  return (
    <SyncDebugPanelWrapper>
      <SyncDebugHeader>
        <SyncDebugTitle>GoCardless sync result</SyncDebugTitle>
        <SyncDebugCloseBtn onClick={onClose}>Close</SyncDebugCloseBtn>
      </SyncDebugHeader>

      <SyncDebugCounters>
        <SyncDebugCounter $accent="#10b981">
          <SyncDebugCounterLabel>Linked (new)</SyncDebugCounterLabel>
          <SyncDebugCounterValue>{result.linked ?? 0}</SyncDebugCounterValue>
        </SyncDebugCounter>
        <SyncDebugCounter $accent="#6366f1">
          <SyncDebugCounterLabel>GC customers fetched</SyncDebugCounterLabel>
          <SyncDebugCounterValue>
            {result.gcCustomersFetched ?? 0}
          </SyncDebugCounterValue>
        </SyncDebugCounter>
        <SyncDebugCounter $accent="#9ca3af">
          <SyncDebugCounterLabel>Companies scanned</SyncDebugCounterLabel>
          <SyncDebugCounterValue>
            {result.companiesScanned ?? 0}
          </SyncDebugCounterValue>
        </SyncDebugCounter>
        <SyncDebugCounter $accent="#9ca3af">
          <SyncDebugCounterLabel>Already linked</SyncDebugCounterLabel>
          <SyncDebugCounterValue>
            {result.alreadyLinked ?? 0}
          </SyncDebugCounterValue>
        </SyncDebugCounter>
        <SyncDebugCounter $accent="#10b981">
          <SyncDebugCounterLabel>Healed → active</SyncDebugCounterLabel>
          <SyncDebugCounterValue>
            {result.healedToActive ?? 0}
          </SyncDebugCounterValue>
        </SyncDebugCounter>
        <SyncDebugCounter $accent="#f59e0b">
          <SyncDebugCounterLabel>No GC match</SyncDebugCounterLabel>
          <SyncDebugCounterValue>{result.noGcMatch ?? 0}</SyncDebugCounterValue>
        </SyncDebugCounter>
        <SyncDebugCounter $accent="#f59e0b">
          <SyncDebugCounterLabel>No mandate</SyncDebugCounterLabel>
          <SyncDebugCounterValue>{result.noMandate ?? 0}</SyncDebugCounterValue>
        </SyncDebugCounter>
        <SyncDebugCounter $accent="#ef4444">
          <SyncDebugCounterLabel>Errors</SyncDebugCounterLabel>
          <SyncDebugCounterValue>{result.errors ?? 0}</SyncDebugCounterValue>
        </SyncDebugCounter>
      </SyncDebugCounters>

      {result.misses && result.misses.length > 0 && (
        <SyncDebugSection>
          <SyncDebugSectionTitle>
            Unlinked companies ({result.misses.length})
          </SyncDebugSectionTitle>
          <SyncDebugTable>
            <thead>
              <tr>
                <th>Company</th>
                <th>Reason</th>
                <th>Tried emails</th>
              </tr>
            </thead>
            <tbody>
              {result.misses.map((m) => (
                <tr key={m.companyId}>
                  <td>{m.companyName || m.companyId}</td>
                  <td>
                    <Pill
                      $tone={
                        m.reason === "no_mandate"
                          ? "warn"
                          : m.reason === "no_email"
                          ? "err"
                          : m.reason === "already_linked"
                          ? "ok"
                          : "muted"
                      }
                    >
                      {m.reason}
                    </Pill>
                  </td>
                  <td style={{ wordBreak: "break-all" }}>
                    {m.triedEmails.length ? m.triedEmails.join(", ") : "(none)"}
                  </td>
                </tr>
              ))}
            </tbody>
          </SyncDebugTable>
        </SyncDebugSection>
      )}

      {result.gcCustomersSummary && result.gcCustomersSummary.length > 0 && (
        <SyncDebugSection>
          <SyncDebugSectionTitle>
            GoCardless customers ({result.gcCustomersSummary.length}
            {result.gcCustomersFetched != null &&
            result.gcCustomersFetched > result.gcCustomersSummary.length
              ? ` of ${result.gcCustomersFetched} — first 200 probed`
              : ""}
            )
          </SyncDebugSectionTitle>
          <SyncDebugTable>
            <thead>
              <tr>
                <th>GC ID</th>
                <th>Email</th>
                <th>Registered email (metadata)</th>
                <th>Raw metadata</th>
                <th>Name</th>
                <th>Mandate</th>
              </tr>
            </thead>
            <tbody>
              {result.gcCustomersSummary.map((c) => {
                const fullName = [c.givenName, c.familyName]
                  .filter(Boolean)
                  .join(" ");
                const metaPairs =
                  c.metadata && Object.keys(c.metadata).length
                    ? Object.entries(c.metadata)
                    : [];
                return (
                  <tr key={c.id}>
                    <td style={{ fontFamily: "monospace", fontSize: 11 }}>
                      {c.id}
                    </td>
                    <td>
                      {c.email || <span style={{ color: "#bbb" }}>—</span>}
                    </td>
                    <td>
                      {c.registeredEmail || (
                        <span style={{ color: "#bbb" }}>—</span>
                      )}
                    </td>
                    <td
                      style={{
                        fontFamily: "monospace",
                        fontSize: 11,
                        maxWidth: 240,
                        wordBreak: "break-all",
                      }}
                    >
                      {metaPairs.length === 0 ? (
                        <span style={{ color: "#bbb" }}>—</span>
                      ) : (
                        metaPairs.map(([k, v]) => (
                          <div key={k}>
                            <span style={{ color: "#6b7280" }}>{k}:</span> {v}
                          </div>
                        ))
                      )}
                    </td>
                    <td>
                      {fullName || c.companyName || (
                        <span style={{ color: "#bbb" }}>—</span>
                      )}
                    </td>
                    <td>
                      {c.hasUsableMandate ? (
                        <Pill $tone="ok">{c.mandateStatus || "active"}</Pill>
                      ) : c.mandateStatus ? (
                        <Pill $tone="warn">{c.mandateStatus}</Pill>
                      ) : (
                        <Pill $tone="err">no mandate</Pill>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </SyncDebugTable>
        </SyncDebugSection>
      )}
    </SyncDebugPanelWrapper>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Invoice preview modal
//
// Shown when the admin clicks the Computed amount on a row. Renders the
// upcoming-invoice line items the cron will charge for at next run,
// along with sub / company context so support can verify "yes, this
// merchant's next invoice will look like THIS." No fetch — the row data
// already carries `preview.lineItems`, so the modal opens instantly.
// ─────────────────────────────────────────────────────────────────────────────

const PreviewBackdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.55);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 24px;
  animation: fadeIn 120ms ease-out;
  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
  @media (max-width: 767px) {
    padding: 0;
    align-items: stretch;
  }
`;

const PreviewCard = styled.div`
  position: relative;
  background: #fff;
  border-radius: 14px;
  width: 100%;
  max-width: 820px;
  max-height: 92vh;
  overflow-y: auto;
  box-shadow: 0 30px 60px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(15, 23, 42, 0.05);
  animation: slideUp 160ms ease-out;
  @keyframes slideUp {
    from {
      transform: translateY(8px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
  @media (max-width: 767px) {
    max-height: 100%;
    border-radius: 0;
    overflow-x: hidden;
  }
`;

// Hero header with subtle gradient + the Subtotal pulled prominently
// to the right so the "answer" is visible in one glance.
const PreviewHero = styled.div`
  padding: 20px 28px 18px;
  background: linear-gradient(135deg, #f8fafc 0%, #eff6ff 100%);
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;
  @media (max-width: 767px) {
    flex-direction: column;
    gap: 10px;
    padding: 16px 48px 14px 16px;
  }
`;

const PreviewHeroLeft = styled.div`
  min-width: 0;
  flex: 1;
`;

const PreviewHeroEyebrow = styled.div`
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #6366f1;
  margin-bottom: 4px;
`;

const PreviewHeroTitle = styled.h3`
  margin: 0 0 6px;
  font-size: 20px;
  font-weight: 700;
  color: #111827;
  line-height: 1.2;
  word-break: break-word;
`;

const PreviewHeroSubtitle = styled.div`
  font-size: 13px;
  color: #4b5563;
`;

const PreviewHeroAmount = styled.div<{ $charging: boolean }>`
  text-align: right;
  flex-shrink: 0;
  @media (max-width: 767px) {
    text-align: left;
  }
`;

const PreviewHeroAmountLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #6b7280;
  margin-bottom: 4px;
`;

const PreviewHeroAmountValue = styled.div<{ $charging: boolean }>`
  font-size: 26px;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  color: ${(p) => (p.$charging ? "#16a34a" : "#9ca3af")};
  line-height: 1;
`;

const PreviewClose = styled.button`
  position: absolute;
  top: 14px;
  right: 14px;
  background: rgba(255, 255, 255, 0.85);
  border: 1px solid #e5e7eb;
  border-radius: 999px;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  line-height: 1;
  cursor: pointer;
  color: #6b7280;
  z-index: 1;
  &:hover {
    background: #fff;
    color: #111827;
  }
`;

const PreviewBody = styled.div`
  padding: 20px 28px 24px;
  @media (max-width: 767px) {
    padding: 16px;
  }
`;

const PreviewSection = styled.div`
  margin-bottom: 22px;
  &:last-child {
    margin-bottom: 0;
  }
`;

const PreviewSectionTitle = styled.div`
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #6b7280;
  margin-bottom: 10px;
`;

const PreviewSectionCard = styled.div`
  background: #f9fafb;
  border: 1px solid #f1f3f5;
  border-radius: 10px;
  padding: 14px 16px;
`;

const PreviewKVGrid = styled.div`
  display: grid;
  grid-template-columns: max-content minmax(0, 1fr);
  gap: 6px 18px;
  font-size: 13px;
  & > div {
    overflow-wrap: anywhere;
  }
  & > div:nth-child(odd) {
    color: #6b7280;
  }
  & > div:nth-child(even) {
    color: #111827;
    font-weight: 500;
  }
`;

// Preview-modal-scoped stat tiles. Named with the `Preview` prefix to
// avoid colliding with the dashboard-level `StatGrid` / `StatCard` /
// `StatLabel` / `StatValue` / `StatSub` styled components used by the
// page totals (defined further up).
const PreviewStatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 10px;
`;

const PreviewStatTile = styled.div<{ $accent?: string }>`
  background: #fff;
  border: 1px solid #f1f3f5;
  border-left: 3px solid ${(p) => p.$accent || "#9ca3af"};
  border-radius: 8px;
  padding: 10px 12px;
`;

const PreviewStatTileLabel = styled.div`
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #6b7280;
`;

const PreviewStatTileValue = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: #111827;
  margin-top: 2px;
  font-variant-numeric: tabular-nums;
`;

const PreviewStatTileSub = styled.div`
  font-size: 11px;
  color: #6b7280;
  margin-top: 2px;
`;

const LineItemsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  background: #fff;
  border: 1px solid #f1f3f5;
  border-radius: 10px;
  overflow: hidden;
  th,
  td {
    padding: 10px 12px;
    text-align: left;
    border-bottom: 1px solid #f1f3f5;
  }
  th {
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #6b7280;
    background: #f9fafb;
    font-weight: 700;
  }
  tbody tr:last-child td {
    border-bottom: 0;
  }
  td.amt,
  th.amt {
    text-align: right;
    font-variant-numeric: tabular-nums;
  }
  @media (max-width: 767px) {
    font-size: 12px;
    th,
    td {
      padding: 8px 6px;
    }
    td.amt {
      white-space: nowrap;
    }
  }
`;

const TotalRow = styled.tr`
  td {
    border-top: 2px solid #111827 !important;
    background: #f9fafb;
    font-weight: 700;
    font-size: 14px;
    padding-top: 12px !important;
  }
`;

const StatusPill = styled.span<{ $tone: "ok" | "warn" | "err" | "muted" }>`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  background: ${(p) =>
    p.$tone === "ok"
      ? "#d1fae5"
      : p.$tone === "warn"
      ? "#fef3c7"
      : p.$tone === "err"
      ? "#fee2e2"
      : "#f3f4f6"};
  color: ${(p) =>
    p.$tone === "ok"
      ? "#065f46"
      : p.$tone === "warn"
      ? "#92400e"
      : p.$tone === "err"
      ? "#991b1b"
      : "#6b7280"};
`;

// Compact "Why this amount" explainer block. Renders the formula or
// the rule (custom fee / tier-based / fixed plan) with the actual
// numbers plugged in so support can answer "why £X?" instantly.
const Explainer = styled.div`
  background: #fef9c3;
  border: 1px solid #fde68a;
  border-radius: 10px;
  padding: 12px 14px;
  font-size: 13px;
  color: #713f12;
  line-height: 1.5;
  & code {
    background: #fff7d6;
    padding: 0 4px;
    border-radius: 3px;
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 12px;
  }
`;

interface PaymentBreakdownProvider {
  provider: string;
  count: number;
  amountPence: number;
}
interface PaymentBreakdownResponse {
  totalCount: number;
  totalAmountPence: number;
  byProvider: PaymentBreakdownProvider[];
  error?: string;
}

// ── Manual override styled bits ─────────────────────────────────────────────

const OverrideForm = styled.div`
  display: grid;
  grid-template-columns: minmax(140px, 200px) 1fr auto;
  gap: 10px;
  align-items: end;
  @media (max-width: 767px) {
    grid-template-columns: 1fr;
  }
`;

const OverrideField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const OverrideLabel = styled.label`
  font-size: 11px;
  font-weight: 600;
  color: #6b7280;
`;

const OverrideInput = styled.input`
  height: 36px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 0 12px;
  font-size: 13px;
  font-family: inherit;
  background: #fff;
  &:focus {
    outline: none;
    border-color: #2563eb;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.15);
  }
`;

const OverrideHint = styled.div`
  font-size: 11px;
  color: #6b7280;
  margin-top: 6px;
`;

const ActiveOverrideBanner = styled.div`
  background: #fef3c7;
  border: 1px solid #fde68a;
  border-radius: 8px;
  padding: 10px 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  font-size: 13px;
  color: #713f12;
  margin-bottom: 10px;
`;

const ManualOverwriteSection: FC<{
  row: UpcomingChargeRow;
  onChanged: () => void;
}> = ({ row, onChanged }) => {
  const { showNotification } = useNotification();
  const [amountStr, setAmountStr] = useState<string>(
    row.manualOverwrite
      ? (row.manualOverwrite.amountPence / 100).toFixed(2)
      : ""
  );
  const [reason, setReason] = useState<string>(
    row.manualOverwrite?.reason ?? ""
  );
  const [busy, setBusy] = useState(false);

  const apply = async () => {
    const parsed = Number(amountStr);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      showNotification(
        "Enter a positive amount in pounds (e.g. 49.99)",
        NotificationTypes.DANGER
      );
      return;
    }
    const amountPence = Math.round(parsed * 100);
    if (
      !window.confirm(
        `Override the next charge to £${(amountPence / 100).toFixed(2)}? ` +
          `On the next cron run we'll bill exactly this amount as a single ` +
          `line item, then clear the override automatically.`
      )
    )
      return;
    setBusy(true);
    try {
      const res = await api.setManualOverwrite(row.companyId, {
        amountPence,
        reason: reason.trim() || undefined,
      });
      if (res?.error) {
        showNotification(res.error, NotificationTypes.DANGER);
        return;
      }
      showNotification("Manual override set", NotificationTypes.SUCCESS);
      onChanged();
    } catch (err) {
      console.error("[ManualOverwrite] apply failed", err);
      showNotification(
        "Failed to set override — check console",
        NotificationTypes.DANGER
      );
    } finally {
      setBusy(false);
    }
  };

  const clear = async () => {
    if (
      !window.confirm(
        "Clear the manual override? The next cron run will use the " +
          "standard computed amount."
      )
    )
      return;
    setBusy(true);
    try {
      const res = await api.clearManualOverwrite(row.companyId);
      if (res?.error) {
        showNotification(res.error, NotificationTypes.DANGER);
        return;
      }
      showNotification("Manual override cleared", NotificationTypes.SUCCESS);
      onChanged();
    } catch (err) {
      console.error("[ManualOverwrite] clear failed", err);
      showNotification(
        "Failed to clear override — check console",
        NotificationTypes.DANGER
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <PreviewSectionCard>
      {row.manualOverwrite && (
        <ActiveOverrideBanner>
          <span>
            <strong>Override active:</strong>{" "}
            {formatPence(row.manualOverwrite.amountPence)}
            {row.manualOverwrite.reason
              ? ` — ${row.manualOverwrite.reason}`
              : ""}
            {row.manualOverwrite.setAt
              ? ` · set ${DateTime.fromISO(row.manualOverwrite.setAt).toFormat(
                  "dd LLL yyyy HH:mm"
                )}`
              : ""}
          </span>
          <Button
            variant={ButtonVariant.OUTLINED}
            onClick={clear}
            loading={busy}
            disabled={busy}
          >
            Clear
          </Button>
        </ActiveOverrideBanner>
      )}
      <OverrideForm>
        <OverrideField>
          <OverrideLabel>Amount (£)</OverrideLabel>
          <OverrideInput
            type="number"
            step="0.01"
            min="0"
            placeholder="e.g. 49.99"
            value={amountStr}
            onChange={(e) => setAmountStr(e.target.value)}
            disabled={busy}
          />
        </OverrideField>
        <OverrideField>
          <OverrideLabel>Reason (optional)</OverrideLabel>
          <OverrideInput
            type="text"
            placeholder="Why are we overriding?"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            disabled={busy}
          />
        </OverrideField>
        <Button onClick={apply} loading={busy} disabled={busy}>
          {row.manualOverwrite ? "Update override" : "Apply override"}
        </Button>
      </OverrideForm>
      <OverrideHint>
        One-shot. The next cron run charges this exact amount and clears the
        override automatically. Following cycles use the standard computed
        amount. Doesn't affect extension packs (they get billed normally on the
        cycle after).
      </OverrideHint>
      {row.computedPreview && (
        <OverrideHint>
          Without the override, the cron would have billed{" "}
          <strong>{formatPence(row.computedPreview.subtotalPence)}</strong> (
          {row.computedPreview.lineItems.length} line item
          {row.computedPreview.lineItems.length === 1 ? "" : "s"}).
        </OverrideHint>
      )}
    </PreviewSectionCard>
  );
};

const formatProvider = (p: string): string => {
  switch (p) {
    case "fena_ob":
      return "Open Banking";
    case "fena_ob_qr":
      return "Open Banking · QR";
    case "fena_mobile":
      return "Mobile";
    case "fena_mobile_touch_to_pay":
      return "Tap-to-pay";
    case "fena_card_payments":
      return "Card (Fena)";
    case "fena_stripe":
      return "Stripe (legacy)";
    case "fena_checkout":
      return "Checkout";
    case "card":
      return "Card";
    case "cash":
      return "Cash";
    case "other":
      return "Other";
    case "none":
      return "None";
    default:
      return p;
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// Tier ladder visualization for PAYMENTS_ONLY tier-based pricing
// ─────────────────────────────────────────────────────────────────────────────
//
// Renders the tier ladder vertically with the highest tier at the top
// (like a thermometer) and the lowest at the bottom. Each tier shows
// its volume ceiling on the left and the flat fee on the right, with a
// colored band in between that progresses from emerald (cheap, low
// volume) up to rose (expensive, high volume). The tier that matches
// the merchant's current volume gets a prominent highlight + a "● You"
// marker showing the actual volume, so an operator can immediately see
// which tier the merchant landed in and how much room they have before
// the next tier kicks in.

const TierLadderWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px 4px;
`;

const TierRow = styled.div<{ $matched: boolean; $hue: number }>`
  display: grid;
  grid-template-columns: 130px 1fr 90px;
  align-items: center;
  gap: 10px;
  padding: ${(p) => (p.$matched ? "10px 12px" : "6px 12px")};
  border-radius: 8px;
  background: ${(p) =>
    p.$matched ? `hsl(${p.$hue} 75% 92%)` : `hsl(${p.$hue} 60% 97%)`};
  border-left: 4px solid
    ${(p) => (p.$matched ? `hsl(${p.$hue} 70% 45%)` : `hsl(${p.$hue} 50% 80%)`)};
  box-shadow: ${(p) =>
    p.$matched
      ? "0 1px 3px rgba(0, 0, 0, 0.08), 0 0 0 1px hsl(0 0% 0% / 0.04)"
      : "none"};
  transition: all 0.15s ease;
`;

const TierLabel = styled.div<{ $matched: boolean }>`
  font-size: ${(p) => (p.$matched ? "13px" : "12px")};
  font-weight: ${(p) => (p.$matched ? 700 : 500)};
  color: #1f2937;
  white-space: nowrap;
`;

const TierBand = styled.div<{ $hue: number; $matched: boolean }>`
  height: ${(p) => (p.$matched ? "18px" : "10px")};
  border-radius: 4px;
  background: linear-gradient(
    90deg,
    hsl(${(p) => p.$hue} 75% 75%),
    hsl(${(p) => p.$hue} 75% 60%)
  );
  position: relative;
  opacity: ${(p) => (p.$matched ? 1 : 0.55)};
`;

const TierVolumeDot = styled.div<{ $left: number }>`
  position: absolute;
  top: 50%;
  left: ${(p) => p.$left}%;
  transform: translate(-50%, -50%);
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: #111827;
  border: 2.5px solid #fff;
  box-shadow: 0 0 0 1.5px #111827, 0 2px 6px rgba(0, 0, 0, 0.25);
  z-index: 2;
`;

const TierVolumeLabel = styled.div<{ $left: number }>`
  position: absolute;
  top: calc(100% + 4px);
  left: ${(p) => p.$left}%;
  transform: translateX(-50%);
  font-size: 11px;
  font-weight: 700;
  color: #111827;
  white-space: nowrap;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  padding: 1px 6px;
`;

const TierPrice = styled.div<{ $matched: boolean }>`
  font-size: ${(p) => (p.$matched ? "14px" : "12px")};
  font-weight: ${(p) => (p.$matched ? 700 : 500)};
  color: ${(p) => (p.$matched ? "#065f46" : "#4b5563")};
  text-align: right;
  white-space: nowrap;
`;

const TierLadderCaption = styled.div`
  font-size: 11px;
  color: #6b7280;
  text-align: center;
  margin-top: 6px;
`;

interface TierLadderProps {
  tiers: Array<{
    upTo: number | null;
    flatAmountPence: number | null;
    unitAmountDecimal: string | null;
  }>;
  volumeMinor: number;
  matchedUpTo: number | null;
}

const TierLadder: FC<TierLadderProps> = ({
  tiers,
  volumeMinor,
  matchedUpTo,
}) => {
  // Sort ascending by `upTo` (null = unbounded top). Reverse for
  // display so the highest tier is rendered at the top.
  const ascending = [...tiers].sort((a, b) => {
    if (a.upTo == null && b.upTo == null) return 0;
    if (a.upTo == null) return 1;
    if (b.upTo == null) return -1;
    return a.upTo - b.upTo;
  });
  const displayOrder = [...ascending].reverse();

  const volumePounds = volumeMinor / 100;

  // Hue progression: emerald-green (cheap) → amber → rose (expensive).
  // Each ascending step gets a slightly cooler→warmer hue.
  const hueForIndex = (idx: number, total: number) => {
    if (total <= 1) return 140;
    // Map 0 → 140 (emerald) and (total-1) → 350 (rose). Lerp.
    const t = idx / (total - 1);
    return 140 + t * 210;
  };

  // Determine matched index in the ascending order (so colors stay stable).
  const matchedIdx = ascending.findIndex(
    (t) =>
      (matchedUpTo == null && t.upTo == null) ||
      (matchedUpTo != null && t.upTo === matchedUpTo)
  );

  return (
    <TierLadderWrap>
      {displayOrder.map((tier) => {
        // Find this tier's position in the ascending order for stable hue.
        const ascIdx = ascending.findIndex((t) => t === tier);
        const hue = hueForIndex(ascIdx, ascending.length);
        const isMatched = ascIdx === matchedIdx && matchedIdx >= 0;

        // Compute "where in this tier's band does the merchant sit"
        // for the dot. For the matched tier, position the dot
        // proportionally between the previous tier's upTo and this
        // tier's upTo. For unbounded, anchor near the start since
        // there's no upper bound.
        let dotLeftPct: number | null = null;
        if (isMatched) {
          const lower = ascIdx > 0 ? ascending[ascIdx - 1].upTo ?? 0 : 0;
          const upper = tier.upTo;
          if (upper == null) {
            // Unbounded — anchor near 10% (just past the top bounded tier).
            dotLeftPct = 10;
          } else {
            const range = upper - lower;
            const pos = range > 0 ? (volumePounds - lower) / range : 0;
            dotLeftPct = Math.max(2, Math.min(98, pos * 100));
          }
        }

        // Label text.
        const upperLabel =
          tier.upTo == null
            ? `over £${(
                ascending[ascending.length - 2]?.upTo ?? 0
              ).toLocaleString("en-GB")}`
            : `up to £${tier.upTo.toLocaleString("en-GB")}`;
        const priceLabel =
          tier.upTo == null
            ? tier.unitAmountDecimal
              ? `${tier.unitAmountDecimal}p/tx`
              : "per-tx"
            : tier.flatAmountPence != null
            ? formatPence(tier.flatAmountPence)
            : "—";

        return (
          <TierRow
            key={`${tier.upTo ?? "unbounded"}`}
            $matched={isMatched}
            $hue={hue}
          >
            <TierLabel $matched={isMatched}>{upperLabel}</TierLabel>
            <TierBand $hue={hue} $matched={isMatched}>
              {dotLeftPct != null && (
                <>
                  <TierVolumeDot $left={dotLeftPct} />
                  <TierVolumeLabel $left={dotLeftPct}>
                    {formatPence(volumeMinor)} volume
                  </TierVolumeLabel>
                </>
              )}
            </TierBand>
            <TierPrice $matched={isMatched}>{priceLabel}</TierPrice>
          </TierRow>
        );
      })}
      <TierLadderCaption>
        Volume {formatPence(volumeMinor)}
        {matchedUpTo != null
          ? ` falls in the "up to £${matchedUpTo.toLocaleString(
              "en-GB"
            )}" tier.`
          : " exceeds all bounded tiers — billed at the top tier + per-tx over-limit rate."}
      </TierLadderCaption>
    </TierLadderWrap>
  );
};

const InvoicePreviewModal: FC<{
  row: UpcomingChargeRow;
  onClose: () => void;
  onChanged: () => void;
}> = ({ row, onClose, onChanged }) => {
  // Close on backdrop click but NOT on card click — standard modal UX.
  const onBackdrop = (e: ReactMouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  };
  // Esc-to-close.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const { showNotification } = useNotification();
  const [chargeBusy, setChargeBusy] = useState(false);
  const [chargeResult, setChargeResult] = useState<{
    success: boolean;
    status: string;
    failureReason?: string;
    requiresAction?: boolean;
  } | null>(null);

  const handleChargeNow = async () => {
    if (
      !window.confirm(
        `Charge ${row.companyName} now? This will find the most recent ` +
          `pending/failed/requires_action invoice and attempt to collect ` +
          `payment via their stored card.`
      )
    )
      return;
    setChargeBusy(true);
    setChargeResult(null);
    try {
      const result = await api.chargeNow(row.companyId);
      setChargeResult(result);
      if (result.success) {
        showNotification("Payment collected", NotificationTypes.SUCCESS);
        onChanged();
      } else if (result.requiresAction) {
        showNotification(
          "3DS required — merchant will receive an email",
          NotificationTypes.WARNING
        );
      } else {
        showNotification(
          result.failureReason || "Charge failed",
          NotificationTypes.DANGER
        );
      }
    } catch (err: unknown) {
      const msg =
        err instanceof Error ? err.message : "Charge failed — check console";
      showNotification(msg, NotificationTypes.DANGER);
      console.error("[ChargeNow]", err);
    } finally {
      setChargeBusy(false);
    }
  };

  const sub = row.subscription;

  // Display period for the preview. Precedence:
  //   1. `sub.currentPeriodStart/End` — the sub doc's authoritative
  //      window. After the recent cutover fixes (`switchSubscription`,
  //      `setInitialBillingConfig`, the period-snap migration), these
  //      ARE interval-correct: monthly subs get a calendar-month
  //      window, yearly subs get a 12-month window. We trust the doc
  //      and use it directly so the modal agrees with what the cron
  //      will actually bill.
  //   2. Fallback when the doc is missing fields: synthesize an
  //      interval-aware calendar window. For yearly subs use the
  //      current calendar year; for monthly use the current calendar
  //      month. Previously this fell back to "current calendar month"
  //      regardless of interval — which produced QA's
  //      "01 May 2026 → 31 May 2026" display on a perfectly healthy
  //      yearly sub even though the cron would bill for the full 12
  //      months recorded in `currentPeriodEnd`.
  const today = DateTime.now();
  const subIntervalRaw = String(
    sub.subscriptionInterval || "MONTH"
  ).toLowerCase();
  const isYearlySub = subIntervalRaw === "year" || subIntervalRaw === "annual";
  const dbStart = sub.currentPeriodStart
    ? DateTime.fromISO(sub.currentPeriodStart)
    : null;
  const dbEnd = sub.currentPeriodEnd
    ? DateTime.fromISO(sub.currentPeriodEnd)
    : null;
  const fallbackStart = isYearlySub
    ? today.startOf("month")
    : today.startOf("month");
  const fallbackEnd = isYearlySub
    ? today
        .startOf("month")
        .plus({ months: 12 })
        .minus({ days: 1 })
        .endOf("day")
    : today.endOf("month");
  const effectiveStart = dbStart ?? fallbackStart;
  const effectiveEnd = dbEnd ?? fallbackEnd;
  const formatDt = (d: DateTime) => d.toFormat("dd LLL yyyy");
  const fmtIso = (iso: string | null) =>
    iso ? DateTime.fromISO(iso).toFormat("dd LLL yyyy") : "—";

  // The "DB period" debug line was previously shown when the hardcoded
  // current-month effective range disagreed with the sub doc — i.e.
  // EVERY healthy yearly sub. Now that we trust the sub doc as the
  // effective period, the only thing worth flagging is genuine
  // staleness (period already expired). Keep the line for ops in that
  // case so it's clear the cron will bill a past window.
  const dbDiffersFromEffective = false;
  const dbStale = dbEnd ? dbEnd < today : false;

  const willCharge = row.willChargeAtNextRun && row.automatedBillingEnabled;

  // Fetch payment breakdown for the effective period (current calendar
  // month). Fires once per modal open. Uses the same `match` shape as
  // the cron's usage accumulator so the totals tie out with the
  // sub.levelUsed / unitCount values.
  const [breakdown, setBreakdown] = useState<PaymentBreakdownResponse | null>(
    null
  );
  const [breakdownLoading, setBreakdownLoading] = useState(true);
  const [breakdownError, setBreakdownError] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    setBreakdownLoading(true);
    setBreakdownError(null);
    api
      .getPaymentBreakdown(
        row.companyId,
        effectiveStart.toUTC().toISO()!,
        effectiveEnd.toUTC().toISO()!
      )
      .then((res: PaymentBreakdownResponse) => {
        if (cancelled) return;
        if (res?.error) {
          setBreakdownError(res.error);
        } else {
          setBreakdown(res);
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const msg =
          err instanceof Error ? err.message : "Failed to load breakdown";
        setBreakdownError(msg);
      })
      .finally(() => {
        if (!cancelled) setBreakdownLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [row.companyId]);

  // Build a human-readable explanation of how the subtotal was computed.
  // We render either:
  //   - "Custom fee" → 1.5% × £X volume + £0.05 × N transactions = £Y
  //     (with floor if customPercentageFeeMinimum is set)
  //   - "Tier-based" → reference the plan tiers used by buildInvoiceLineItems
  //   - "Fixed plan" → "TOOLKIT BASIC monthly = £19.99 (after X% company discount)"
  //   - "No charge yet" for trialing / zero subtotal
  const cp = row.companyPricing;
  const explainerLines: string[] = [];
  if (sub.status === "trialing") {
    explainerLines.push(
      "Customer is on trial — no charge until trial ends. The first invoice on trial-end will be prorated for the remaining period."
    );
  } else if (row.preview.subtotalPence <= 0) {
    explainerLines.push(
      "Subtotal is £0 — invoice will be skipped by the cron's `subtotalPence <= 0` guard."
    );
  } else if (sub.subscriptionOption === "PAYMENTS_ONLY") {
    if (
      cp.customPercentageFee != null ||
      cp.customFlatFeePerTransaction != null
    ) {
      const pct =
        cp.customPercentageFee != null ? `${cp.customPercentageFee}%` : null;
      const flat =
        cp.customFlatFeePerTransaction != null
          ? formatPence(cp.customFlatFeePerTransaction)
          : null;
      const parts: string[] = [];
      if (pct) parts.push(`${pct} of ${formatPence(sub.levelUsed)} volume`);
      if (flat) parts.push(`${flat} × ${sub.unitCount} transactions`);
      let formula = parts.join(" + ");
      if (cp.customPercentageFeeMinimum != null) {
        formula += ` · floored at ${formatPence(
          cp.customPercentageFeeMinimum
        )}`;
      }
      if (formula)
        explainerLines.push(
          `Custom fee: ${formula} = ${formatPence(row.preview.subtotalPence)}.`
        );
    } else {
      explainerLines.push(
        `Tier-based PAYMENTS_ONLY${
          cp.hasHighRiskSubscriptionPricing ? " (high-risk pricing)" : ""
        }: applied to ${formatPence(sub.levelUsed)} volume / ${
          sub.unitCount
        } transactions this period.`
      );
    }
  } else {
    // TOOLKIT / TOOLKIT_MOBILE — fixed plan price
    const interval =
      String(sub.subscriptionInterval || "month").toLowerCase() === "year"
        ? "annual"
        : "monthly";
    let line = `Fixed plan price: ${sub.subscriptionOption}${
      sub.subscriptionLevel ? " " + sub.subscriptionLevel : ""
    } ${interval}`;
    if (cp.discountPercentOff) {
      line += ` with ${cp.discountPercentOff}% company discount`;
      if (cp.discountDuration === "repeating" && cp.discountDurationInMonths) {
        line += ` (${cp.discountDurationInMonths} months)`;
      } else if (cp.discountDuration === "forever") {
        line += " (forever)";
      }
    }
    line += ` = ${formatPence(row.preview.subtotalPence)}.`;
    explainerLines.push(line);
  }
  if (cp.hasBespokePricing) {
    explainerLines.push(
      "Note: company has bespoke pricing flagged on the company doc."
    );
  }

  return (
    <PreviewBackdrop onClick={onBackdrop}>
      <PreviewCard role="dialog" aria-label="Invoice preview">
        <PreviewClose onClick={onClose} aria-label="Close">
          ×
        </PreviewClose>
        <PreviewHero>
          <PreviewHeroLeft>
            <PreviewHeroEyebrow>Invoice preview</PreviewHeroEyebrow>
            <PreviewHeroTitle>{row.companyName}</PreviewHeroTitle>
            <PreviewHeroSubtitle>
              {sub.subscriptionOption}
              {sub.subscriptionLevel
                ? ` · ${sub.subscriptionLevel}`
                : ""} ·{" "}
              {String(sub.subscriptionInterval || "month").toLowerCase()}
              {" · "}
              {formatDt(effectiveStart)} → {formatDt(effectiveEnd)}
            </PreviewHeroSubtitle>
          </PreviewHeroLeft>
          <PreviewHeroAmount $charging={willCharge}>
            <PreviewHeroAmountLabel>Subtotal</PreviewHeroAmountLabel>
            <PreviewHeroAmountValue $charging={willCharge}>
              {formatPence(row.preview.subtotalPence)}
            </PreviewHeroAmountValue>
          </PreviewHeroAmount>
        </PreviewHero>

        <PreviewBody>
          {/* ── Status pills ── */}
          <PreviewSection>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {willCharge ? (
                <StatusPill $tone="ok">Will charge at next cron run</StatusPill>
              ) : !row.automatedBillingEnabled ? (
                <StatusPill $tone="warn">Auto-charge disabled</StatusPill>
              ) : (
                <StatusPill $tone="muted">
                  Preview only — period not yet ended
                </StatusPill>
              )}
              {sub.status === "trialing" && (
                <StatusPill $tone="muted">On trial</StatusPill>
              )}
              {sub.cancelAtPeriodEnd && (
                <StatusPill $tone="warn">Cancellation scheduled</StatusPill>
              )}
              {row.needsInitialSetup && (
                <StatusPill $tone="warn">Needs initial setup</StatusPill>
              )}
              {dbStale && (
                <StatusPill $tone="err">
                  DB period stale — run initial setup
                </StatusPill>
              )}
              {row.manualOverwrite && (
                <StatusPill $tone="warn">
                  Manual override pending —{" "}
                  {formatPence(row.manualOverwrite.amountPence)}
                </StatusPill>
              )}
            </div>
          </PreviewSection>

          {/* ── Why this amount ── */}
          {explainerLines.length > 0 && (
            <PreviewSection>
              <PreviewSectionTitle>Why this amount</PreviewSectionTitle>
              <Explainer>
                {explainerLines.map((line, i) => (
                  <div key={i}>{line}</div>
                ))}
              </Explainer>
            </PreviewSection>
          )}

          {/* ── Usage stat tiles (PAYMENTS_ONLY only) ── */}
          {sub.subscriptionOption === "PAYMENTS_ONLY" && (
            <PreviewSection>
              <PreviewSectionTitle>Usage this period</PreviewSectionTitle>
              <PreviewStatGrid>
                <PreviewStatTile $accent="#7c3aed">
                  <PreviewStatTileLabel>Volume</PreviewStatTileLabel>
                  <PreviewStatTileValue>
                    {formatPence(sub.levelUsed)}
                  </PreviewStatTileValue>
                  <PreviewStatTileSub>processed</PreviewStatTileSub>
                </PreviewStatTile>
                <PreviewStatTile $accent="#f59e0b">
                  <PreviewStatTileLabel>Transactions</PreviewStatTileLabel>
                  <PreviewStatTileValue>{sub.unitCount}</PreviewStatTileValue>
                  <PreviewStatTileSub>this period</PreviewStatTileSub>
                </PreviewStatTile>
                <PreviewStatTile $accent={breakdown ? "#16a34a" : "#9ca3af"}>
                  <PreviewStatTileLabel>Confirmed (paid)</PreviewStatTileLabel>
                  <PreviewStatTileValue>
                    {breakdownLoading
                      ? "…"
                      : breakdown
                      ? breakdown.totalCount
                      : "—"}
                  </PreviewStatTileValue>
                  <PreviewStatTileSub>
                    {breakdown
                      ? formatPence(breakdown.totalAmountPence) + " volume"
                      : breakdownError || "loading"}
                  </PreviewStatTileSub>
                </PreviewStatTile>
              </PreviewStatGrid>
            </PreviewSection>
          )}

          {/* ── Per-provider payment breakdown ── */}
          <PreviewSection>
            <PreviewSectionTitle>
              Payments processed (current month)
            </PreviewSectionTitle>
            {breakdownLoading ? (
              <PreviewSectionCard>
                <span style={{ fontSize: 13, color: "#6b7280" }}>Loading…</span>
              </PreviewSectionCard>
            ) : breakdownError ? (
              <PreviewSectionCard>
                <span style={{ fontSize: 13, color: "#991b1b" }}>
                  Failed to load: {breakdownError}
                </span>
              </PreviewSectionCard>
            ) : !breakdown || breakdown.totalCount === 0 ? (
              <PreviewSectionCard>
                <span style={{ fontSize: 13, color: "#6b7280" }}>
                  No PAID payments in this period.
                </span>
              </PreviewSectionCard>
            ) : (
              <LineItemsTable>
                <thead>
                  <tr>
                    <th>Provider</th>
                    <th className="amt">Count</th>
                    <th className="amt">Volume</th>
                    <th className="amt">% of total</th>
                  </tr>
                </thead>
                <tbody>
                  {breakdown.byProvider.map((p) => (
                    <tr key={p.provider}>
                      <td>{formatProvider(p.provider)}</td>
                      <td className="amt">{p.count}</td>
                      <td className="amt">{formatPence(p.amountPence)}</td>
                      <td className="amt">
                        {breakdown.totalAmountPence > 0
                          ? `${Math.round(
                              (p.amountPence / breakdown.totalAmountPence) * 100
                            )}%`
                          : "—"}
                      </td>
                    </tr>
                  ))}
                  <TotalRow>
                    <td>Total</td>
                    <td className="amt">{breakdown.totalCount}</td>
                    <td className="amt">
                      {formatPence(breakdown.totalAmountPence)}
                    </td>
                    <td className="amt">100%</td>
                  </TotalRow>
                </tbody>
              </LineItemsTable>
            )}
          </PreviewSection>

          {/* ── Tier ladder visualization (PAYMENTS_ONLY tier-based) ── */}
          {row.preview.tierBreakdown &&
            row.preview.tierBreakdown.tiers.length > 0 && (
              <PreviewSection>
                <PreviewSectionTitle>
                  Tier ladder{" "}
                  {row.preview.tierBreakdown.isHighRisk
                    ? "(High-risk pricing)"
                    : "(Standard pricing)"}
                </PreviewSectionTitle>
                <TierLadder
                  tiers={row.preview.tierBreakdown.tiers}
                  volumeMinor={row.preview.tierBreakdown.volumeMinor}
                  matchedUpTo={row.preview.tierBreakdown.matchedUpTo}
                />
              </PreviewSection>
            )}

          {/* ── Line items the cron will produce ── */}
          <PreviewSection>
            <PreviewSectionTitle>Invoice line items</PreviewSectionTitle>
            {row.preview.lineItems.length === 0 ? (
              <PreviewSectionCard>
                <span style={{ fontSize: 13, color: "#6b7280" }}>
                  No line items — invoice would be skipped (the cron's{" "}
                  <code>subtotalPence &lt;= 0</code> guard).
                </span>
              </PreviewSectionCard>
            ) : (
              <LineItemsTable>
                <thead>
                  <tr>
                    <th>Description</th>
                    <th className="amt">Qty</th>
                    <th className="amt">Unit</th>
                    <th className="amt">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {row.preview.lineItems.map((li, idx) => (
                    <tr key={idx}>
                      <td>
                        {li.description}
                        {li.type ? (
                          <>
                            {" "}
                            <StatusPill $tone="muted">{li.type}</StatusPill>
                          </>
                        ) : null}
                      </td>
                      <td className="amt">{li.quantity}</td>
                      <td className="amt">{formatPence(li.unitAmountPence)}</td>
                      <td className="amt">{formatPence(li.totalPence)}</td>
                    </tr>
                  ))}
                  {/* Subtotal / VAT / Total breakdown. The backend now
                       returns `netSubtotalPence` (pre-VAT, post-discount)
                       and `vatPence` (20% UK VAT, 0 for custom-percentage
                       PAYMENTS_ONLY and bespoke merchants). `subtotalPence`
                       is VAT-inclusive — what the cron / provider charges. */}
                  {row.preview.vatPence != null && row.preview.vatPence > 0 ? (
                    <>
                      <TotalRow>
                        <td colSpan={3}>Subtotal (net)</td>
                        <td className="amt">
                          {formatPence(
                            row.preview.netSubtotalPence ??
                              row.preview.subtotalPence
                          )}
                        </td>
                      </TotalRow>
                      <TotalRow>
                        <td colSpan={3}>VAT (20%)</td>
                        <td className="amt">
                          {formatPence(row.preview.vatPence)}
                        </td>
                      </TotalRow>
                      <TotalRow>
                        <td colSpan={3}>
                          <strong>Total (incl. VAT)</strong>
                        </td>
                        <td className="amt">
                          <strong>
                            {formatPence(row.preview.subtotalPence)}
                          </strong>
                        </td>
                      </TotalRow>
                    </>
                  ) : (
                    <TotalRow>
                      <td colSpan={3}>Subtotal</td>
                      <td className="amt">
                        {formatPence(row.preview.subtotalPence)}
                      </td>
                    </TotalRow>
                  )}
                </tbody>
              </LineItemsTable>
            )}
          </PreviewSection>

          {/* ── Subscription details ── */}
          <PreviewSection>
            <PreviewSectionTitle>Subscription</PreviewSectionTitle>
            <PreviewSectionCard>
              <PreviewKVGrid>
                <div>Pricing rule</div>
                <div>{row.pricingRule || "—"}</div>
                {row.interimInterval && (
                  <>
                    <div>Interim interval</div>
                    <div>{row.interimInterval}</div>
                  </>
                )}
                <div>Sub status</div>
                <div>{sub.status}</div>
                <div>Last EoP charge</div>
                <div>
                  {sub.lastEndOfPeriodChargedAt
                    ? DateTime.fromISO(sub.lastEndOfPeriodChargedAt).toFormat(
                        "dd LLL yyyy HH:mm"
                      )
                    : "—"}
                </div>
                {dbDiffersFromEffective && (
                  <>
                    <div>DB period</div>
                    <div
                      style={{
                        color: dbStale ? "#92400e" : "#6b7280",
                        fontWeight: 500,
                      }}
                    >
                      {fmtIso(sub.currentPeriodStart)} →{" "}
                      {fmtIso(sub.currentPeriodEnd)}
                      {dbStale && " · stale"}
                    </div>
                  </>
                )}
              </PreviewKVGrid>
            </PreviewSectionCard>
          </PreviewSection>

          {/* ── Company pricing config ── */}
          <PreviewSection>
            <PreviewSectionTitle>Pricing configuration</PreviewSectionTitle>
            <PreviewSectionCard>
              <PreviewKVGrid>
                <div>Custom percentage fee</div>
                <div>
                  {cp.customPercentageFee != null
                    ? `${cp.customPercentageFee}%`
                    : "—"}
                </div>
                <div>Custom flat fee per transaction</div>
                <div>
                  {cp.customFlatFeePerTransaction != null
                    ? formatPence(cp.customFlatFeePerTransaction)
                    : "—"}
                </div>
                <div>Custom percentage minimum</div>
                <div>
                  {cp.customPercentageFeeMinimum != null
                    ? formatPence(cp.customPercentageFeeMinimum)
                    : "—"}
                </div>
                <div>Discount</div>
                <div>
                  {cp.discountPercentOff != null
                    ? `${cp.discountPercentOff}% (${
                        cp.discountDuration ?? "—"
                      }${
                        cp.discountDuration === "repeating" &&
                        cp.discountDurationInMonths
                          ? `, ${cp.discountDurationInMonths} mo`
                          : ""
                      })`
                    : "—"}
                </div>
                <div>Trial period</div>
                <div>
                  {cp.trialPeriodMode === "none"
                    ? "disabled"
                    : cp.trialPeriodMode === "custom" && cp.trialPeriodDays
                    ? `custom · ${cp.trialPeriodDays} days`
                    : "default"}
                </div>
                <div>High-risk pricing</div>
                <div>{cp.hasHighRiskSubscriptionPricing ? "yes" : "no"}</div>
                <div>Bespoke pricing</div>
                <div>{cp.hasBespokePricing ? "yes" : "no"}</div>
              </PreviewKVGrid>
            </PreviewSectionCard>
          </PreviewSection>

          {/* ── Payment method ── */}
          <PreviewSection>
            <PreviewSectionTitle>Payment method</PreviewSectionTitle>
            <PreviewSectionCard>
              <div style={{ fontSize: 13 }}>
                {row.paymentMethod ? (
                  <>
                    {row.paymentMethod.provider === "gocardless"
                      ? "Direct Debit (GoCardless)"
                      : row.paymentMethod.provider === "ryft"
                      ? "Fena Card Payments"
                      : row.paymentMethod.provider}
                    {row.paymentMethod.last4 && (
                      <> · ····{row.paymentMethod.last4}</>
                    )}
                    {row.paymentMethod.brand && (
                      <> · {row.paymentMethod.brand}</>
                    )}
                    {row.paymentMethod.expiryMonth &&
                      row.paymentMethod.expiryYear && (
                        <>
                          {" "}
                          · expires{" "}
                          {String(row.paymentMethod.expiryMonth).padStart(
                            2,
                            "0"
                          )}
                          /{row.paymentMethod.expiryYear}
                        </>
                      )}{" "}
                    ·{" "}
                    <StatusPill
                      $tone={
                        row.paymentMethod.status === "active" ? "ok" : "warn"
                      }
                    >
                      {row.paymentMethod.status}
                    </StatusPill>
                  </>
                ) : (
                  <StatusPill $tone="err">No payment method on file</StatusPill>
                )}
              </div>
            </PreviewSectionCard>
          </PreviewSection>

          {/* ── Charge now ── */}
          {row.paymentMethod?.provider === "ryft" && (
            <PreviewSection>
              <PreviewSectionTitle>Charge now</PreviewSectionTitle>
              <PreviewSectionCard>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    flexWrap: "wrap",
                  }}
                >
                  <Button
                    onClick={handleChargeNow}
                    loading={chargeBusy}
                    disabled={chargeBusy || !row.paymentMethod}
                  >
                    {chargeBusy ? "Charging…" : "Charge now"}
                  </Button>
                  <span style={{ fontSize: 12, color: "#6b7280" }}>
                    Finds the most recent pending/failed invoice and attempts to
                    collect via the stored card.
                  </span>
                </div>
                {chargeResult && (
                  <div
                    style={{
                      marginTop: 10,
                      padding: "8px 12px",
                      borderRadius: 8,
                      fontSize: 13,
                      background: chargeResult.success
                        ? "#d1fae5"
                        : chargeResult.requiresAction
                        ? "#fef3c7"
                        : "#fee2e2",
                      color: chargeResult.success
                        ? "#065f46"
                        : chargeResult.requiresAction
                        ? "#92400e"
                        : "#991b1b",
                    }}
                  >
                    {chargeResult.success
                      ? `Paid — invoice ${chargeResult.status}`
                      : chargeResult.requiresAction
                      ? "3DS authentication required — merchant will receive an email to complete payment"
                      : `Failed: ${
                          chargeResult.failureReason || chargeResult.status
                        }`}
                  </div>
                )}
              </PreviewSectionCard>
            </PreviewSection>
          )}

          {/* ── Manual override ──
              Lets ops bill a custom amount on the next cron run instead
              of the computed total. On apply, the cron treats this as a
              one-shot override: bill exactly the specified amount as a
              single line item, then clear the flag. Subsequent cycles
              fall back to standard automated billing. */}
          <PreviewSection>
            <PreviewSectionTitle>Manual override</PreviewSectionTitle>
            <ManualOverwriteSection row={row} onChanged={onChanged} />
          </PreviewSection>
        </PreviewBody>
      </PreviewCard>
    </PreviewBackdrop>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

const UpcomingChargesDashboard: FC = () => {
  const { showNotification } = useNotification();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  // Desktop keeps the dashboard open in its own tab; on mobile a new tab is a
  // dead end (no history for the details page's Back), so stay in this one.
  const openCompanyDetails = (companyId: string) => {
    const url = `${AppRoutes.Private.Companies.DETAILS}${companyId}`;
    if (isMobile) navigate(url);
    else window.open(url, "_blank");
  };
  const [data, setData] = useState<UpcomingChargesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<"all" | "enabled" | "pending">("all");
  const [search, setSearch] = useState("");
  // Client-side pagination over `visibleRows`. The backend returns the
  // full filtered set in one fetch (242-ish rows is small enough), and
  // the page slice is computed in `pagedRows` below. We keep "select
  // all visible" operating on the entire filtered set rather than the
  // current page — admins reported "I selected page 1 but the bulk
  // toggle only flipped page 1, so I had to repeat for every page",
  // which made paginated bulk ops worse than no pagination at all.
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [now, setNow] = useState(() => DateTime.now());
  const [togglingId, setTogglingId] = useState<string | null>(null);
  // Selection set for bulk actions. Tracks companyIds of checked rows.
  // Cleared on every refresh / sync / verify so we never act on stale
  // ids whose underlying data has changed.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  // Row whose invoice preview the admin clicked. Holds the whole row so
  // the modal renders without an extra fetch.
  const [previewRow, setPreviewRow] = useState<UpcomingChargeRow | null>(null);
  const [setupRow, setSetupRow] = useState<UpcomingChargeRow | null>(null);
  const [syncing, setSyncing] = useState(false);
  // Last sync result — kept in state so we can render a debug panel below
  // the table after the admin clicks Sync. Lets you see exactly what GC
  // returned and which Fena companies failed to link, with the emails the
  // sync tried for each, without needing the browser console.
  const [lastSyncResult, setLastSyncResult] = useState<any | null>(null);

  // Live countdown tick.
  useEffect(() => {
    const t = setInterval(() => setNow(DateTime.now()), 1000);
    return () => clearInterval(t);
  }, []);

  // Stable refresh — see dashboard-loop fix history. showNotification is
  // ref-stashed so changing identity doesn't retrigger the load effect.
  const notifyRef = useRef(showNotification);
  notifyRef.current = showNotification;

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      const result =
        (await api.getUpcomingCharges()) as UpcomingChargesResponse;
      setData(result);
      // Clear bulk-selection on every refresh — a row may now have a
      // different state than when it was checked, and we don't want the
      // bulk bar acting on stale ids.
      setSelectedIds(new Set());
    } catch (err) {
      console.error("[UpcomingChargesDashboard] load failed", err);
      notifyRef.current(
        "Failed to load upcoming charges",
        NotificationTypes.DANGER
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleToggle = async (companyId: string, nextEnabled: boolean) => {
    setTogglingId(companyId);
    setData((prev) =>
      prev
        ? {
            ...prev,
            rows: prev.rows.map((r) =>
              r.companyId === companyId
                ? { ...r, automatedBillingEnabled: nextEnabled }
                : r
            ),
          }
        : prev
    );
    try {
      await api.setAutomatedBilling(companyId, nextEnabled);
      refresh();
    } catch (err) {
      console.error("[UpcomingChargesDashboard] toggle failed", err);
      showNotification(
        "Failed to update flag — try again",
        NotificationTypes.DANGER
      );
      setData((prev) =>
        prev
          ? {
              ...prev,
              rows: prev.rows.map((r) =>
                r.companyId === companyId
                  ? { ...r, automatedBillingEnabled: !nextEnabled }
                  : r
              ),
            }
          : prev
      );
    } finally {
      setTogglingId(null);
    }
  };

  const downloadCsv = async () => {
    try {
      // Forward the dashboard's current search + filter pill so the CSV
      // matches what's visible. Previously the export ignored both and
      // dumped every row, which testers reported as "I searched for X,
      // saw 3 rows, downloaded and got everyone".
      const url = api.getUpcomingChargesCsvUrl({
        search: search || undefined,
        filter,
      });
      const res = await api.fetcher(url, { method: "GET" });
      if (!res.ok) {
        showNotification(
          `CSV download failed (${res.status})`,
          NotificationTypes.DANGER
        );
        return;
      }
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = `upcoming-charges-${new Date()
        .toISOString()
        .slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // Revoke after a tick so Safari has time to bind the click
      setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    } catch (err) {
      console.error("[UpcomingChargesDashboard] CSV download failed", err);
      showNotification(
        "CSV download failed — check console",
        NotificationTypes.DANGER
      );
    }
  };

  const runSync = async () => {
    if (
      !window.confirm(
        "Sync with GoCardless? This walks all GC customers and links any " +
          "company that has a customer + active mandate but no local payment " +
          "method record yet. Safe to re-run."
      )
    )
      return;
    setSyncing(true);
    try {
      const result = await api.syncGoCardless();
      if (result?.error) {
        showNotification(
          result.error || "Sync failed",
          NotificationTypes.DANGER
        );
        return;
      }
      const linked = result?.linked ?? 0;
      const noMatch = result?.noGcMatch ?? 0;
      const noMandate = result?.noMandate ?? 0;
      const alreadyLinked = result?.alreadyLinked ?? 0;
      showNotification(
        `Linked ${linked} new · ${alreadyLinked} already linked · ` +
          `${noMatch} no GC match · ${noMandate} no mandate`,
        NotificationTypes.SUCCESS
      );
      // Persist the full result to state so the debug panel can render
      // it. Also dump to console for easy copy/paste in support tickets.
      setLastSyncResult(result);
      // eslint-disable-next-line no-console
      console.log("[GC Sync] full response", result);
      refresh();
    } catch (err) {
      console.error("[UpcomingChargesDashboard] sync failed", err);
      showNotification("Sync failed — check console", NotificationTypes.DANGER);
    } finally {
      setSyncing(false);
    }
  };

  // One-shot cleanup of legacy zero-amount invoices. They're noise from
  // earlier dev runs that pre-dated the cron's `subtotalPence <= 0`
  // guard. Modern code paths can't create them, but they linger and
  // clutter merchant billing-history views.
  const runCleanupZeroInvoices = async () => {
    if (
      !window.confirm(
        "Soft-delete every £0.00 invoice in the database? Legacy noise " +
          "from before the zero-amount guard. The records stay in the DB " +
          "(deletedAt set) so audit trails are preserved. Safe to re-run."
      )
    )
      return;
    try {
      const result = await api.cleanupZeroInvoices();
      if (result?.error) {
        showNotification(
          result.error || "Cleanup failed",
          NotificationTypes.DANGER
        );
        return;
      }
      const modified = result?.modified ?? 0;
      // Distinguish "I cleaned X" from "nothing to clean". QA reported
      // that "Soft-deleted 0 zero-amount invoices" reads as a failure
      // when it's actually a no-op — the user was expecting £0
      // upcoming-charge ROWS to disappear (which this action doesn't
      // touch; it cleans legacy BillingInvoice rows, not projected
      // upcoming charges). Clearer copy below.
      showNotification(
        modified === 0
          ? "No legacy zero-amount invoices left to clean up."
          : `Soft-deleted ${modified} zero-amount invoice${
              modified === 1 ? "" : "s"
            }`,
        NotificationTypes.SUCCESS
      );
      // Refresh the dashboard so any rows whose backing invoices were
      // cleaned drop out of the list immediately, instead of waiting
      // for a manual reload.
      try {
        await refresh();
      } catch {
        /* non-fatal */
      }
    } catch (err) {
      console.error("[UpcomingChargesDashboard] cleanup failed", err);
      showNotification(
        "Cleanup failed — check console",
        NotificationTypes.DANGER
      );
    }
  };

  // Verify pass: walks every existing gocardless link and removes the ones
  // whose GC customer email doesn't EXACTLY match any of the linked Fena
  // company's emails. Used to clean up false positives left over from the
  // previous (now-removed) +suffix-stripping matcher.
  const runVerify = async () => {
    if (
      !window.confirm(
        "Verify and clean up GoCardless links? This re-checks every " +
          "linked company with EXACT email matching and removes any link " +
          "where the GC customer's email doesn't actually match. Use this " +
          "after a sync that may have produced false positives."
      )
    )
      return;
    setSyncing(true);
    try {
      const result = await api.verifyGoCardlessLinks();
      if (result?.error) {
        showNotification(
          result.error || "Verify failed",
          NotificationTypes.DANGER
        );
        return;
      }
      const checked = result?.checked ?? 0;
      const kept = result?.kept ?? 0;
      const removed = result?.removed ?? 0;
      showNotification(
        `Checked ${checked} · kept ${kept} · removed ${removed}`,
        NotificationTypes.SUCCESS
      );
      setLastSyncResult(result);
      // eslint-disable-next-line no-console
      console.log("[GC Verify] full response", result);
      refresh();
    } catch (err) {
      console.error("[UpcomingChargesDashboard] verify failed", err);
      showNotification(
        "Verify failed — check console",
        NotificationTypes.DANGER
      );
    } finally {
      setSyncing(false);
    }
  };

  // ── Bulk-toggle helpers ──────────────────────────────────────────────────
  // Selection set is cleared after every successful bulk action and after
  // refresh, so a re-render against new data can't accidentally act on a
  // companyId that's since left the visible set.
  const clearSelection = () => setSelectedIds(new Set());

  const toggleRowSelected = (companyId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(companyId)) next.delete(companyId);
      else next.add(companyId);
      return next;
    });
  };

  const runBulkToggle = async (enabled: boolean) => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    if (
      !window.confirm(
        `${enabled ? "Enable" : "Disable"} auto-charge for ${ids.length} ` +
          `compan${ids.length === 1 ? "y" : "ies"}? You can re-toggle ` +
          `individual rows after.`
      )
    )
      return;
    setBulkBusy(true);
    // Optimistic update so the UI reacts immediately. We roll back on
    // network failure below.
    setData((prev) =>
      prev
        ? {
            ...prev,
            rows: prev.rows.map((r) =>
              selectedIds.has(r.companyId)
                ? { ...r, automatedBillingEnabled: enabled }
                : r
            ),
          }
        : prev
    );
    try {
      const result = await api.setAutomatedBillingBulk(ids, enabled);
      if (result?.error) {
        showNotification(
          result.error || "Bulk update failed",
          NotificationTypes.DANGER
        );
        return;
      }
      const modified = result?.modified ?? 0;
      const matched = result?.matched ?? 0;
      showNotification(
        `${enabled ? "Enabled" : "Disabled"} auto-charge — ` +
          `${modified} updated · ${matched - modified} already in target state`,
        NotificationTypes.SUCCESS
      );
      clearSelection();
      refresh();
    } catch (err) {
      console.error("[UpcomingChargesDashboard] bulk toggle failed", err);
      showNotification(
        "Bulk update failed — try again",
        NotificationTypes.DANGER
      );
      // Pull authoritative state back from the server so optimistic
      // changes don't drift.
      refresh();
    } finally {
      setBulkBusy(false);
    }
  };

  const renderPaymentMethod = (pm: UpcomingChargeRow["paymentMethod"]) => {
    if (!pm) {
      return <span style={{ color: "#bbb", fontSize: 12 }}>—</span>;
    }
    const label =
      pm.provider === "gocardless"
        ? "Direct Debit"
        : pm.provider === "ryft"
        ? "Card"
        : pm.provider === "manual"
        ? "Manual"
        : pm.provider;
    const variant =
      pm.status === "active"
        ? "active"
        : pm.status === "failed"
        ? "failed"
        : "warning";
    const cardSuffix =
      pm.provider === "ryft" && pm.last4
        ? ` ····${pm.last4}${
            pm.expiryMonth && pm.expiryYear
              ? ` ${String(pm.expiryMonth).padStart(2, "0")}/${String(
                  pm.expiryYear
                ).slice(-2)}`
              : ""
          }`
        : "";
    return (
      <SubTag $variant={variant}>
        {label}
        {cardSuffix}
      </SubTag>
    );
  };

  const nextRun = useMemo(
    () => (data ? nextCronAt(data.nextCronRun) : null),
    [data]
  );
  const countdown = nextRun ? nextRun.diff(now) : null;
  const urgent = (countdown?.toMillis() ?? Infinity) < 1000 * 60 * 60 * 24;

  const visibleRows = useMemo(() => {
    if (!data) return [];
    const q = search.trim().toLowerCase();
    return data.rows.filter((r) => {
      if (filter === "enabled" && !r.automatedBillingEnabled) return false;
      if (filter === "pending" && r.automatedBillingEnabled) return false;
      if (q && !r.companyName.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [data, filter, search]);

  // Totals — when the user has applied a search or a filter pill, the
  // header chips need to reflect what's actually visible, not the
  // whole-dataset totals returned by the server. Recompute from
  // `visibleRows` when any filter is active; fall back to the server
  // totals when the view is unfiltered (preserves the "X / Y companies"
  // ratio the original layout uses).
  const isFiltered = !!search.trim() || filter !== "all";
  const totals = useMemo(() => {
    const serverTotals = data?.totals;
    if (!isFiltered || !serverTotals) return serverTotals;
    const enabled = visibleRows.filter((r) => r.automatedBillingEnabled);
    const pending = visibleRows.filter((r) => !r.automatedBillingEnabled);
    const enabledTotalPence = enabled.reduce(
      (acc: number, r: any) => acc + (r?.computedTotalPence ?? 0),
      0
    );
    const enabledChargingCompanies = enabled.filter(
      (r: any) => (r?.computedTotalPence ?? 0) > 0
    ).length;
    // Same shape as the server-side totals for the pending side —
    // backlog of un-enabled rows that would bill if flipped on now.
    // Falls back to summing computedTotalPence (per-row preview) since
    // the row payload exposes that field, mirroring how the enabled
    // side recomputes here under the search/filter.
    const pendingTotalPence = pending.reduce(
      (acc: number, r: any) =>
        acc + (r?.willChargeAtNextRun ? r?.computedTotalPence ?? 0 : 0),
      0
    );
    const pendingChargingCompanies = pending.filter(
      (r: any) => r?.willChargeAtNextRun
    ).length;
    return {
      ...serverTotals,
      enabledTotalPence,
      enabledChargingCompanies,
      enabledCompanies: enabled.length,
      pendingCompanies: pending.length,
      pendingTotalPence,
      pendingChargingCompanies,
      totalCompanies: visibleRows.length,
    };
  }, [data?.totals, isFiltered, visibleRows]);

  // Reset to page 1 whenever the filtered set changes — otherwise the
  // user can be left on "page 7" when their new search narrows the
  // result to a single page, producing an empty table until they
  // notice the pagination footer. We watch `visibleRows.length`
  // rather than `[search, filter]` so the reset also fires when the
  // server returns a fresh `data` payload after a refresh.
  useEffect(() => {
    setPage(1);
  }, [visibleRows.length, perPage]);

  // Current page slice. Bulk select + select-all still operate on
  // `visibleRows` (the full filtered set), not `pagedRows`, so the
  // pagination is purely a render concern.
  const pagedRows = useMemo(() => {
    const start = (page - 1) * perPage;
    return visibleRows.slice(start, start + perPage);
  }, [visibleRows, page, perPage]);

  // Visible-row selection helpers for the checkbox column header. The
  // "select all" header checkbox toggles every CURRENTLY VISIBLE row
  // (i.e. respects the search box + filter pills); rows hidden by the
  // active filter aren't touched. This is the standard "smart select-
  // all" behaviour admins expect.
  const visibleIds = useMemo(
    () => visibleRows.map((r) => r.companyId),
    [visibleRows]
  );
  const allVisibleSelected =
    visibleIds.length > 0 && visibleIds.every((id) => selectedIds.has(id));
  const someVisibleSelected =
    !allVisibleSelected && visibleIds.some((id) => selectedIds.has(id));

  const toggleSelectAllVisible = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        for (const id of visibleIds) next.delete(id);
      } else {
        for (const id of visibleIds) next.add(id);
      }
      return next;
    });
  };

  // ─── Columns ────────────────────────────────────────────────────────────
  // Defined inline (not memoized on `togglingId`/`setupRow`/`handleToggle`
  // change) — small data set, micro-optimisation isn't worth the dep churn.
  const columns: Array<ColumnDef<UpcomingChargeRow, any>> = [
    {
      id: "select",
      header: () => (
        <input
          type="checkbox"
          aria-label={
            allVisibleSelected ? "Deselect all visible" : "Select all visible"
          }
          checked={allVisibleSelected}
          ref={(el) => {
            if (el) el.indeterminate = someVisibleSelected;
          }}
          onChange={toggleSelectAllVisible}
        />
      ),
      cell: ({ row: { original } }) => (
        <Cell>
          <input
            type="checkbox"
            aria-label={`Select ${original.companyName}`}
            checked={selectedIds.has(original.companyId)}
            onChange={() => toggleRowSelected(original.companyId)}
          />
        </Cell>
      ),
    },
    {
      header: "Company",
      accessorKey: "companyName",
      meta: { mobileTitle: true },
      cell: ({ row: { original } }) => (
        <Cell>
          <CompanyLinkBtn
            onClick={() => openCompanyDetails(original.companyId)}
          >
            {original.companyName}
          </CompanyLinkBtn>
        </Cell>
      ),
    },
    {
      header: "Plan",
      accessorKey: "subscription.subscriptionOption",
      cell: ({ row: { original } }) => {
        // PAYMENTS_ONLY's `subscriptionLevel` is a legacy artifact
        // (Stripe-era BASIC/STARTER/etc) — meaningless for the new
        // self-managed PAYMENTS_ONLY product, which is differentiated
        // by `hasHighRiskSubscriptionPricing` instead (Standard vs
        // High Risk). TOOLKIT / TOOLKIT_MOBILE still use the
        // subscriptionLevel as the meaningful tier (BASIC, STARTER,
        // SCALE_UP, PROFESSIONAL, ENTERPRISE).
        const isPaymentsOnly =
          original.subscription.subscriptionOption === "PAYMENTS_ONLY";
        const suffix = isPaymentsOnly
          ? ` · ${
              original.companyPricing?.hasHighRiskSubscriptionPricing
                ? "High Risk"
                : "Standard"
            }`
          : original.subscription.subscriptionLevel
          ? ` · ${original.subscription.subscriptionLevel}`
          : "";
        return (
          <Cell>
            <TypeTag
              $bg={getTypeColor(original.subscription.subscriptionOption)}
            >
              {getTypeLabel(original.subscription.subscriptionOption)}
              {suffix}
            </TypeTag>
          </Cell>
        );
      },
    },
    {
      header: "Period",
      accessorKey: "subscription.currentPeriodEnd",
      cell: ({ row: { original } }) => (
        <Cell style={{ whiteSpace: "nowrap" }}>
          <DateCell>
            {fmtDate(original.subscription.currentPeriodStart)} →{" "}
            {fmtDate(original.subscription.currentPeriodEnd)}
          </DateCell>
          {original.subscription.subscriptionOption === "PAYMENTS_ONLY" && (
            <DateSub>
              {/* `levelUsed` is in PENCE (Fena billing convention) —
                  use formatPence which divides by 100 and adds the
                  £ prefix + locale-grouped thousand separators. */}
              {formatPence(original.subscription.levelUsed ?? 0)} ·{" "}
              {original.subscription.unitCount ?? 0} tx
            </DateSub>
          )}
        </Cell>
      ),
    },
    {
      header: "Pricing rule",
      accessorKey: "pricingRule",
      cell: ({ row: { original } }) => {
        const text = `${original.pricingRule}${
          original.interimInterval ? ` · ${original.interimInterval}` : ""
        }`;
        return (
          <Cell
            title={text}
            style={{
              maxWidth: 220,
              minWidth: 0,
              overflow: "hidden",
            }}
          >
            <SubTag
              style={{
                display: "inline-block",
                maxWidth: "100%",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                verticalAlign: "middle",
              }}
            >
              {text}
            </SubTag>
          </Cell>
        );
      },
    },
    {
      header: "Computed (incl. VAT)",
      accessorKey: "preview.subtotalPence",
      cell: ({ row: { original } }) => {
        const charging =
          original.willChargeAtNextRun && original.automatedBillingEnabled;
        const override = original.manualOverwrite;
        // Surface the pre-discount base amount + the discount when one
        // applies, so a £0.00 row with a 100%-off discount is
        // self-explanatory instead of looking like a missing-price bug.
        // The backend pushes the subscription fee as a positive line
        // item and the discount as a negative one — derive both here.
        const lineItems: any[] = original.preview?.lineItems ?? [];
        const discountItem = lineItems.find(
          (li) => li?.type === "discount" || (li?.totalPence ?? 0) < 0
        );
        const baseFeeItem = lineItems.find(
          (li) => li?.type === "subscription_fee" && (li?.totalPence ?? 0) > 0
        );
        const hasDiscount =
          !!discountItem && !!original.companyPricing?.discountPercentOff;
        const discountPct = original.companyPricing?.discountPercentOff;
        const discountDuration = original.companyPricing?.discountDuration;
        const discountMonths =
          original.companyPricing?.discountDurationInMonths;
        const durLabel =
          discountDuration === "forever"
            ? "forever"
            : discountDuration === "repeating" && discountMonths
            ? `for ${discountMonths} mo`
            : discountDuration === "once"
            ? "one-off"
            : null;
        const basePence = baseFeeItem?.totalPence ?? null;
        return (
          <Cell>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                gap: 3,
              }}
            >
              <ComputedAmountBtn
                type="button"
                $charging={charging}
                onClick={() => setPreviewRow(original)}
                title={
                  override
                    ? `Manual override pending — click for details${
                        override.reason ? `: ${override.reason}` : ""
                      }`
                    : hasDiscount && basePence != null
                    ? `Base ${formatPence(
                        basePence
                      )} − ${discountPct}% discount${
                        durLabel ? ` (${durLabel})` : ""
                      } = ${formatPence(original.preview.subtotalPence)}`
                    : original.preview.vatPence != null &&
                      original.preview.vatPence > 0
                    ? `Net ${formatPence(
                        original.preview.netSubtotalPence ??
                          original.preview.subtotalPence -
                            original.preview.vatPence
                      )} + ${formatPence(
                        original.preview.vatPence
                      )} VAT (20%) = ${formatPence(
                        original.preview.subtotalPence
                      )}`
                    : "Click to see the invoice breakdown"
                }
              >
                {formatPence(original.preview.subtotalPence)}
              </ComputedAmountBtn>
              {/* VAT subtitle — the headline is already VAT-inclusive
                  (`subtotalPence` from the backend), but operators
                  shouldn't have to guess. Surface "incl. £X VAT" so
                  what they see in the cell matches what the cron
                  actually charges. */}
              {!override &&
                original.preview.vatPence != null &&
                original.preview.vatPence > 0 && (
                  <span
                    style={{
                      fontSize: 10,
                      color: "#6b7280",
                      whiteSpace: "nowrap",
                      lineHeight: 1.2,
                    }}
                    title={`Net ${formatPence(
                      original.preview.netSubtotalPence ??
                        original.preview.subtotalPence -
                          original.preview.vatPence
                    )} + ${formatPence(original.preview.vatPence)} VAT (20%)`}
                  >
                    incl. {formatPence(original.preview.vatPence)} VAT
                  </span>
                )}
              {override && (
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: 0.5,
                    color: "#92400e",
                    background: "#fef3c7",
                    border: "1px solid #fde68a",
                    borderRadius: 999,
                    padding: "1px 8px",
                    textTransform: "uppercase",
                  }}
                  title={
                    override.reason
                      ? `Override reason: ${override.reason}`
                      : undefined
                  }
                >
                  Override
                </span>
              )}
              {!override && hasDiscount && basePence != null && (
                <span
                  style={{
                    fontSize: 11,
                    color: "#6b7280",
                    whiteSpace: "nowrap",
                    lineHeight: 1.2,
                  }}
                  title={`Base ${formatPence(basePence)} − ${discountPct}% off${
                    durLabel ? ` (${durLabel})` : ""
                  }`}
                >
                  <span
                    style={{
                      textDecoration: "line-through",
                      color: "#9ca3af",
                    }}
                  >
                    {formatPence(basePence)}
                  </span>{" "}
                  <span style={{ color: "#059669", fontWeight: 600 }}>
                    −{discountPct}%
                  </span>
                </span>
              )}
            </div>
          </Cell>
        );
      },
    },
    {
      header: "Payment method",
      accessorKey: "paymentMethod",
      cell: ({ row: { original } }) => (
        <Cell>{renderPaymentMethod(original.paymentMethod)}</Cell>
      ),
    },
    {
      header: "Status",
      accessorKey: "willChargeAtNextRun",
      cell: ({ row: { original } }) => {
        const variant = original.subscription.cancelAtPeriodEnd
          ? "warning"
          : !original.automatedBillingEnabled
          ? "default"
          : original.willChargeAtNextRun
          ? "active"
          : "default";
        const label = original.subscription.cancelAtPeriodEnd
          ? "Cancels at period end"
          : !original.automatedBillingEnabled
          ? "Auto-charge off"
          : original.willChargeAtNextRun
          ? "Will charge"
          : "Period not due";
        return (
          <Cell>
            <SubTag $variant={variant}>{label}</SubTag>
          </Cell>
        );
      },
    },
    {
      header: "Auto-charge",
      accessorKey: "automatedBillingEnabled",
      cell: ({ row: { original } }) => (
        <Cell style={{ textAlign: "center" }}>
          <Switch $checked={original.automatedBillingEnabled}>
            <input
              type="checkbox"
              checked={original.automatedBillingEnabled}
              disabled={togglingId === original.companyId}
              onChange={() =>
                handleToggle(
                  original.companyId,
                  !original.automatedBillingEnabled
                )
              }
            />
          </Switch>
        </Cell>
      ),
    },
    {
      header: "",
      id: "actions",
      cell: ({ row: { original } }) => (
        <Cell>
          <ActionGroup>
            {original.needsInitialSetup && (
              <PrimaryIconButton
                onClick={() => setSetupRow(original)}
                title="Run initial cutover setup"
              >
                Setup
              </PrimaryIconButton>
            )}
            <IconButton
              onClick={() => openCompanyDetails(original.companyId)}
              title="Open company details to customise"
            >
              Edit
            </IconButton>
          </ActionGroup>
        </Cell>
      ),
    },
  ];

  return (
    <AnimateAppearanceWrapper>
      <Container>
        <TopLine>
          <PageTitle>Upcoming charges</PageTitle>
        </TopLine>

        {/* ── Scope notice ─────────────────────────────────────────────── */}
        <ScopeNotice>
          <ScopeNoticeIcon>i</ScopeNoticeIcon>
          <div>
            This dashboard shows only{" "}
            <ScopeNoticeStrong>active and trialing</ScopeNoticeStrong>{" "}
            subscriptions — these are the only ones the billing cron will charge
            on its next run. Subscriptions in <em>processing</em>,{" "}
            <em>payment_failed</em>, <em>past_due</em>, <em>incomplete</em>,{" "}
            <em>canceled</em>, or <em>deleted</em> states need remediation
            elsewhere — see the{" "}
            <ScopeNoticeStrong>Subscriptions</ScopeNoticeStrong> dashboard for
            the full fleet view.
          </div>
        </ScopeNotice>

        {/* ── Banner ───────────────────────────────────────────────────── */}
        <Banner>
          <div>
            <BannerLabel>Next billing run</BannerLabel>
            <BannerScheduleLine>
              {nextRun
                ? nextRun.toFormat("EEEE, d LLL yyyy 'at' HH:mm 'BST'")
                : "—"}
            </BannerScheduleLine>
            <BannerCountdown $urgent={urgent}>
              {formatCountdown(countdown)} · 1st of every month at{" "}
              {data?.nextCronRun.hour ?? 16}
              :00 {data?.nextCronRun.timezone ?? "Europe/London"}
            </BannerCountdown>
          </div>
          <StatGrid>
            <StatCard $accent="#16a34a">
              <StatLabel>Will charge (net)</StatLabel>
              <StatValue>
                {totals
                  ? formatPenceShort(
                      totals.enabledNetPence ??
                        totals.enabledTotalPence - (totals.enabledVatPence ?? 0)
                    )
                  : "—"}
              </StatValue>
              <StatSub
                title={
                  totals
                    ? `Net ${formatPence(
                        totals.enabledNetPence ??
                          totals.enabledTotalPence -
                            (totals.enabledVatPence ?? 0)
                      )} + ${formatPence(
                        totals.enabledVatPence ?? 0
                      )} VAT = ${formatPence(totals.enabledTotalPence)} gross`
                    : undefined
                }
              >
                {totals?.enabledChargingCompanies ?? 0} compan
                {totals?.enabledChargingCompanies === 1 ? "y" : "ies"} ready
                {totals && (totals.enabledVatPence ?? 0) > 0 && (
                  <>
                    {" · "}+{formatPenceShort(totals.enabledVatPence ?? 0)} VAT
                  </>
                )}
              </StatSub>
            </StatCard>
            {/*
              Cutover-backlog tile — sizes the £ volume sitting behind
              the `automatedBillingEnabled = false` gate. Operator
              question this answers: "if we flipped on every held-back
              row right now, what would the next cron tick collect?"
              Counts only rows where `willChargeAtNextRun` is true so
              it's apples-to-apples with "Will charge" — yearly subs
              with periods 9 months out are intentionally excluded.
              Amber when there's something to act on, neutral grey
              when the backlog is empty (everyone's already enabled).
            */}
            <StatCard
              $accent={
                (totals?.pendingChargingCompanies ?? 0) > 0
                  ? "#f59e0b"
                  : "#9ca3af"
              }
            >
              <StatLabel>Pending to collect (net)</StatLabel>
              <StatValue>
                {totals
                  ? formatPenceShort(
                      totals.pendingNetPence ??
                        totals.pendingTotalPence - (totals.pendingVatPence ?? 0)
                    )
                  : "—"}
              </StatValue>
              <StatSub
                title={
                  totals
                    ? `Net ${formatPence(
                        totals.pendingNetPence ??
                          totals.pendingTotalPence -
                            (totals.pendingVatPence ?? 0)
                      )} + ${formatPence(
                        totals.pendingVatPence ?? 0
                      )} VAT = ${formatPence(totals.pendingTotalPence)} gross`
                    : undefined
                }
              >
                {totals?.pendingChargingCompanies ?? 0} compan
                {totals?.pendingChargingCompanies === 1 ? "y" : "ies"} waiting
                on enable
                {totals && (totals.pendingVatPence ?? 0) > 0 && (
                  <>
                    {" · "}+{formatPenceShort(totals.pendingVatPence ?? 0)} VAT
                  </>
                )}
              </StatSub>
            </StatCard>
            <StatCard $accent="#027aff">
              <StatLabel>Enabled</StatLabel>
              <StatValue>
                {totals?.enabledCompanies ?? 0} / {totals?.totalCompanies ?? 0}
              </StatValue>
              <StatSub>flag set to true</StatSub>
            </StatCard>
            <StatCard
              $accent={
                (totals?.pendingCompanies ?? 0) > 0 ? "#f59e0b" : "#9ca3af"
              }
            >
              <StatLabel>Need attention</StatLabel>
              <StatValue>{totals?.pendingCompanies ?? 0}</StatValue>
              <StatSub>not yet enabled</StatSub>
            </StatCard>
          </StatGrid>
        </Banner>

        {/* ── Table ────────────────────────────────────────────────────── */}
        <TableContainer>
          <TableHeader>
            <TableHeaderLeft>
              <FilterPill
                $active={filter === "all"}
                onClick={() => setFilter("all")}
              >
                All ({totals?.totalCompanies ?? 0})
              </FilterPill>
              <FilterPill
                $active={filter === "enabled"}
                $color="#00bc82"
                onClick={() => setFilter("enabled")}
              >
                Enabled ({totals?.enabledCompanies ?? 0})
              </FilterPill>
              <FilterPill
                $active={filter === "pending"}
                $color="#f9a000"
                onClick={() => setFilter("pending")}
              >
                Pending ({totals?.pendingCompanies ?? 0})
              </FilterPill>
              <SearchInput
                placeholder="Search company..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </TableHeaderLeft>
            <TableHeaderRight>
              <Button
                variant={ButtonVariant.OUTLINED}
                onClick={runSync}
                loading={syncing}
                title="Find existing GoCardless customers + mandates and back-link them to companies"
              >
                Sync GoCardless
              </Button>
              <Button
                variant={ButtonVariant.OUTLINED}
                onClick={runVerify}
                loading={syncing}
                title="Re-check every GC link with exact email matching and remove false positives"
              >
                Verify links
              </Button>
              <Button
                variant={ButtonVariant.OUTLINED}
                onClick={runCleanupZeroInvoices}
                title="Soft-delete legacy £0.00 invoices that pre-dated the cron's zero-amount guard"
              >
                Clean £0 invoices
              </Button>
              <Button variant={ButtonVariant.OUTLINED} onClick={downloadCsv}>
                Download CSV
              </Button>
              <Button
                variant={ButtonVariant.OUTLINED}
                onClick={refresh}
                loading={loading}
              >
                Refresh
              </Button>
            </TableHeaderRight>
          </TableHeader>

          {/* Bulk action bar — only renders when there's a selection.
              Stays above the table so the admin can see counts AND the
              button targets without scrolling. */}
          {selectedIds.size > 0 && (
            <BulkBar>
              <BulkBarLeft>
                <strong>{selectedIds.size}</strong> selected
                {visibleIds.length > selectedIds.size && (
                  <span style={{ color: "#6b7280" }}>
                    {" "}
                    ({visibleIds.length} visible)
                  </span>
                )}
              </BulkBarLeft>
              <BulkBarRight>
                <Button
                  variant={ButtonVariant.CONTAINED}
                  onClick={() => runBulkToggle(true)}
                  loading={bulkBusy}
                  disabled={bulkBusy}
                  title="Set automatedBillingEnabled=true for every selected company"
                >
                  Enable auto-charge
                </Button>
                <Button
                  variant={ButtonVariant.OUTLINED}
                  onClick={() => runBulkToggle(false)}
                  loading={bulkBusy}
                  disabled={bulkBusy}
                  title="Set automatedBillingEnabled=false for every selected company"
                >
                  Disable auto-charge
                </Button>
                <Button
                  variant={ButtonVariant.OUTLINED}
                  onClick={clearSelection}
                  disabled={bulkBusy}
                >
                  Clear
                </Button>
              </BulkBarRight>
            </BulkBar>
          )}

          {loading && !data ? (
            <LoadingWrap>Loading...</LoadingWrap>
          ) : data ? (
            <ResponsiveWrapper>
              <ReactTableComponent columns={columns} data={pagedRows as any} />
            </ResponsiveWrapper>
          ) : (
            <LoadingWrap>
              <Typography variant={TypographyVariant.BODY6}>
                Failed to load data
              </Typography>
            </LoadingWrap>
          )}
        </TableContainer>
        {/*
          Paginator lives OUTSIDE `TableContainer` because that wrapper
          uses `overflow: hidden` (sharedTableStyles.tsx) to clip wide
          rows into the inner `ResponsiveWrapper`'s horizontal scroll.
          The rows-per-page dropdown is `position: absolute` and opens
          downward — when the Paginator was nested inside TableContainer
          the menu was clipped to the container's border, so admins
          couldn't pick anything beyond the default 20 rows. Rendering
          it as a sibling keeps the dropdown free of the clipping box.
        */}
        {data && visibleRows.length > 0 && (
          <Paginator
            totalDocs={visibleRows.length}
            currentPage={page}
            perPage={perPage}
            onPaginationChange={(p, l) => {
              setPerPage(l);
              setPage(p);
            }}
          />
        )}
      </Container>

      {previewRow && (
        <InvoicePreviewModal
          row={previewRow}
          onClose={() => setPreviewRow(null)}
          onChanged={() => {
            // Pull fresh data — manual override changes the row's
            // computed amount and pill state.
            setPreviewRow(null);
            refresh();
          }}
        />
      )}

      {lastSyncResult && (
        <SyncDebugPanel
          result={lastSyncResult}
          onClose={() => setLastSyncResult(null)}
        />
      )}

      {setupRow && (
        <InitialBillingSetupModal
          row={{
            companyId: setupRow.companyId,
            companyName: setupRow.companyName,
            subscriptionOption: setupRow.subscription.subscriptionOption,
            subscriptionLevel: setupRow.subscription.subscriptionLevel,
            subscriptionInterval: setupRow.subscription.subscriptionInterval,
            customFlatFeePerTransaction:
              setupRow.companyPricing.customFlatFeePerTransaction,
            customPercentageFee: setupRow.companyPricing.customPercentageFee,
            customPercentageFeeMinimum:
              setupRow.companyPricing.customPercentageFeeMinimum,
            paymentsInterimBillingInterval:
              setupRow.companyPricing.paymentsInterimBillingInterval,
            hasBespokePricing: setupRow.companyPricing.hasBespokePricing,
            hasHighRiskSubscriptionPricing:
              setupRow.companyPricing.hasHighRiskSubscriptionPricing,
            discountPercentOff: setupRow.companyPricing.discountPercentOff,
            discountDuration: setupRow.companyPricing.discountDuration,
            discountDurationInMonths:
              setupRow.companyPricing.discountDurationInMonths,
            trialPeriodMode: setupRow.companyPricing.trialPeriodMode,
            trialPeriodDays: setupRow.companyPricing.trialPeriodDays,
          }}
          onClose={() => setSetupRow(null)}
          onApplied={refresh}
        />
      )}
    </AnimateAppearanceWrapper>
  );
};

export default UpcomingChargesDashboard;
